import { resolveActiveAuditOverrides, stripOverrideVersionSuffix } from '../../lib/audit/truthAttestations';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Activity, AlertTriangle, ArrowUpRight, Check, CheckCheck, CheckCircle2, ChevronDown, ChevronRight, FileText, ListTree, Loader2, Lock, MapPin, RefreshCw, ShieldCheck, ThumbsUp, Wrench, X } from 'lucide-react';
import type { SiteEntity } from '../../types/site';
import { resolveSiteDocumentFileName } from '../../lib/site/resolveSiteDocuments';
import type { SiteDocument, SiteRagReport } from '../../types/siteRag';
import { documentBodyText, documentNeedsBodyHydration, hydrateDocumentBodiesForEvidence } from '../../lib/documentBody';
import type { UserAuditFinding, UserAuditResult } from '../../types/userAudit';
import { useStore } from '../../store';
import { Button } from '../ui/button';
import { StatusPill, Chip, EmptyState } from '../ui/aurora';
import { relativeTime } from '../ui/SaveIndicator';
import { AUDIT_DEBOUNCE_MS, makeStableFindingKey, aggregateSeverityCounts } from '../../lib/audit/runUserAudit';
import { resolveSiteDecisionStage } from '../../lib/stage2/siteDecisionStage';
import {
  resolveFindingFix,
  resolveSecondaryFix,
  type FindingFix,
  type FindingFixContext,
} from '../../lib/audit/findingFixResolver';
import { buildIntentSyncPatches } from '../../lib/audit/intentSedReconciler';
import { PRECEDENCE_FACT_LABELS } from '../../lib/audit/precedenceResolution';
import { isUnavailableFloodPrecedence } from '../../lib/audit/floodZoneAvailability';
import { ResolveAmendmentDialog, type ResolveAmendmentDraft } from './decision/ResolveAmendmentDialog';
import { deriveTrust, trustHeadline, type TrustDimensionId, type TrustDimensionSummary } from '../../lib/audit/trustScore';
import { getRerunDefaults } from '../../lib/rerunDefaults';
import { reportReaderPath } from '../../lib/siteNavigation';
import { CaveatHonestyPanel } from './audit/CaveatHonestyPanel';
import { AmendmentsAuditPanel } from './audit/AmendmentsAuditPanel';
import { UserAuthoredConflictsPanel } from './audit/UserAuthoredConflictsPanel';
import { QualitativeClaimAuditPanel } from './audit/QualitativeClaimAuditPanel';
import { CitationSupportPanel } from './audit/CitationSupportPanel';
import { SectionCoherencePanel } from './audit/SectionCoherencePanel';
import { ProviderRelevancePanel } from './audit/ProviderRelevancePanel';
import { ParcelVariancePanel } from './audit/ParcelVariancePanel';
import { ParcelTopologyPanel } from './audit/ParcelTopologyPanel';
import { ResolverSnapshotPanel } from './audit/ResolverSnapshotPanel';
import { verifyQuoteCitations } from '../../lib/audit/citationIntegrityVerifier';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import { ReportQualityAuditSection } from './ReportQualityAuditSection';
import '../../styles/auroraSurfaces.css';

/** Presentation only: preserve the complete stored audit and its canonical gates. */
export function auditFindingIsStageRelevant(finding: UserAuditFinding, reports: readonly SiteRagReport[], stage: 1 | 2): boolean {
  if (stage === 2) return true;
  if (finding.checkId === 'R-FA-SCEN') return false;
  const type = finding.reportType ?? reports.find(report => report.id === finding.reportId)?.reportType;
  if (!type || REPORT_TYPE_REGISTRY[type as ReportTypeId]?.stage !== 2) return true;
  // Missing/thin future report content is deferred. Actual blocking data,
  // source, citation and numerical errors remain actionable at either stage.
  const futureCompleteness = ['R-73', 'tier-2l', 'tier-2b', 'tier-4'].includes(finding.checkId);
  return finding.severity === 'block' && !futureCompleteness;
}

export function auditResultForStage(result: UserAuditResult, reports: readonly SiteRagReport[], stage: 1 | 2): UserAuditResult {
  if (stage === 2) return result;
  const findings = result.findings.filter(finding => auditFindingIsStageRelevant(finding, reports, stage));
  const reportIds = new Set(reports.filter(report => REPORT_TYPE_REGISTRY[(report.reportType ?? 'feasibility') as ReportTypeId]?.stage !== 2).map(report => report.id));
  const scoped = <T,>(rows: Record<string, T> | undefined) => rows == null ? rows
    : Object.fromEntries(Object.entries(rows).filter(([id]) => reportIds.has(id)));
  return { ...result, findings, severityCounts: aggregateSeverityCounts(findings),
    reportIds: result.reportIds.filter(id => reportIds.has(id)),
    precedenceByReport: scoped(result.precedenceByReport),
    qualitativeClaimByReport: scoped(result.qualitativeClaimByReport),
    sectionCoherenceByReport: scoped(result.sectionCoherenceByReport),
    citationSupportByReport: scoped(result.citationSupportByReport) };
}

/** Per-report group cap — "Show all" lifts it per group. */
const GROUP_CAP = 20;
const TABLE_MAX_HEIGHT = 560;

/**
 * #112 — render the REPORT type in full, human-readable form instead of the
 * raw slug (e.g. "power-availability" → "Detailed Power Availability Report"
 * instead of a truncated "power-avail…"). `reportType` is a free-form string,
 * so resolve it through the registry when it matches a known id; otherwise
 * humanize the slug (strip separators, title-case) as a safe fallback.
 */
function reportTypeLabel(reportType: string | undefined): string {
  if (!reportType) return '—';
  const known = REPORT_TYPE_REGISTRY[reportType as ReportTypeId];
  if (known) return known.label;
  return reportType
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function severityClass(severity: UserAuditFinding['severity']): string {
  switch (severity) {
    case 'block': return 'text-danger font-semibold';
    case 'warn': return 'text-warning font-medium';
    default: return 'text-text-muted';
  }
}

/**
 * Severity → Aurora tone tokens for the findings pill + leading status dot.
 * Returns CSS var()s (NOT bg-* utility classes) so the pill flips dark/light
 * correctly AND sidesteps the verdict-dot sentinel (which only flags bg-* class
 * names co-occurring with rounded-full/dot in a tier/verdict context). The old
 * hardcoded pastel fills never flipped — they stayed bright on the dark theme.
 */
function severityTone(severity: UserAuditFinding['severity']): { bg: string; fg: string; dot: string } {
  switch (severity) {
    case 'block': return { bg: 'var(--danger-subtle)', fg: 'var(--danger)', dot: 'var(--danger)' };
    case 'warn': return { bg: 'var(--warning-subtle)', fg: 'var(--warning)', dot: 'var(--warning)' };
    default: return { bg: 'var(--surface-2)', fg: 'var(--text-secondary)', dot: 'var(--text-muted)' };
  }
}

/* ════════════════════════════════════════════════════════════════════════
   DATA-TRUST CONSOLE — plain-language presentation layer (Aurora Cockpit).
   The underlying audit checks STILL RUN exactly as before; this only
   re-buckets the findings into four user-meaningful trust dimensions and a
   single 0–100 trust score, so the surface no longer leaks internal rule
   codes (R-65/67/70/71/72/78/79, tier-2e, raw check-ids …) to the user.

   SP4 S3 — the trust derivation itself (deriveTrust + the dimension
   taxonomy) now lives in the pure, unit-tested module `lib/audit/trustScore`.
   It is byte-identical to the prior inline implementation (all per-tier /
   per-fact / per-check multipliers default to 1.0). Only the UI-side bits
   (icons, CSS tone classes) remain here.
   ════════════════════════════════════════════════════════════════════════ */

const TRUST_DIMENSION_ICON: Record<TrustDimensionId, typeof AlertTriangle> = {
  conflicts: AlertTriangle,
  provenance: ListTree,
  citation: CheckCheck,
  intent: Check,
};

const SIGNAL_TONE_CLASS: Record<TrustDimensionSummary['tone'], string> = {
  clean: 'signal--clean',
  warn: 'signal--warn',
  block: 'signal--block',
};

/** One plain-language trust-signal ledger row (replaces raw rule-id labels). */
function TrustSignalRow({ dim }: { dim: TrustDimensionSummary }) {
  const Icon = TRUST_DIMENSION_ICON[dim.id];
  return (
    <div className={`signal ${SIGNAL_TONE_CLASS[dim.tone]}`} data-testid={`audit-signal-${dim.id}`}>
      <span className="signal__ico"><Icon className="w-3.5 h-3.5" aria-hidden /></span>
      <span>
        <span className="signal__name">{dim.name}</span>
        <span className="signal__sub">{dim.sub}</span>
      </span>
      <span className="signal__count">
        {dim.count}
        <small>{dim.note}</small>
      </span>
    </div>
  );
}



/**
 * U6 — derive an actionable control descriptor for one finding INLINE (no
 * cross-module helper dependency). Keyed by check type:
 *  - reconcile   → internal-number inconsistency (tier-2e / R-67): value-picker.
 *  - navigate    → finding carries a deep-link (report editor / doc / financial).
 *  - view-detail → only an expandable payload; surface the existing expand.
 *  - none        → nothing to do.
 */

/** Internal-number inconsistency checks that should offer a Reconcile control. */
/* isReconcilableFinding / parseReconcileCandidates moved to
   src/lib/audit/findingFixResolver.ts — the pure fix-dispatch module. */

/**
 * P3 — build the report-reader deep-link for a finding's paragraph anchor.
 *
 * Resolves to the SPA report route `/sites/{siteId}/reports/{reportId}` with a
 * `#section-{sectionId}` hash that the report view's scroll/highlight handler
 * (reused H-4/#47 passage-offset infra) consumes to scroll + highlight the
 * offending passage. Returns null unless we have BOTH a target report id (the
 * finding's own report, else the freshest matching-type report) AND an anchor
 * with a `sectionId` — so a finding without a resolvable anchor falls through
 * to its existing action (additive, non-breaking).
 */
function buildAnchorDeepLink(
  f: UserAuditFinding,
  siteId: string,
  siteReports: SiteRagReport[],
): string | null {
  const sectionId = f.anchor?.sectionId;
  if (!sectionId) return null;
  const targetReport =
    (f.reportId ? siteReports.find((r) => r.id === f.reportId) : undefined) ??
    (f.reportType
      ? [...siteReports]
          .filter((r) => (r.reportType ?? 'feasibility') === f.reportType)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
      : undefined);
  if (!targetReport) return null;
  return `${reportReaderPath(siteId, targetReport.id)}#section-${encodeURIComponent(sectionId)}`;
}

/* deriveFindingAction replaced by resolveFindingFix (findingFixResolver.ts):
   every check family now maps to ONE executable primary action. */

/**
 * P — display formatting for canonical values: a raw parsed float
 * ("1170.1054573547272") reads as garbage in the picker and the decisions
 * ledger. Numeric values render with thousands separators and ≤1 decimal;
 * the RECORDED value stays exact (formatting is display-only).
 */
function formatCanonicalValue(v: string): string {
  const n = Number(v.replace(/,/g, ''));
  if (!Number.isFinite(n) || v.trim() === '') return v;
  return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

/* ──────────────────────────────────────────────────────────────────────
 * STRUCTURED CONFLICT SUMMARY — the human-readable face of a value finding.
 *
 * The raw check output ("nearestSubstation: report=is 2, tier1=TAP143264
 * (HIFLD)", five-sentence Tier-3 run-ons) is a debug string, not a decision
 * aid. Value-bearing findings now render as a comparison strip — what the
 * reports say vs the verified/source-of-truth value, each side labelled and
 * chip-formatted — with the settled state as a badge. The raw message stays
 * available verbatim inside the expanded payload (fidelity is never lost);
 * findings with no parsable structure fall back to the plain-message hero.
 * ────────────────────────────────────────────────────────────────────── */

interface ConflictView {
  /** Human fact name ("Nearest substation"), from the shared label table. */
  factLabel: string;
  /** Short verb phrase completing the headline. */
  headline: string;
  /** Left side — the value(s) the report(s) cite. */
  cited: Array<{ value: string; source?: string }>;
  /** Right side — the verified / source-of-truth value, when one exists. */
  truth?: { value: string; source: string };
  /** Report types that disagree with the truth (cross-report only). */
  dissenters?: string[];
  /** Recorded canonical that settles this fact, when present. */
  settledTo?: string;
}

function factLabelFor(f: UserAuditFinding): string | undefined {
  return f.canonicalFactKey
    ? PRECEDENCE_FACT_LABELS[f.canonicalFactKey] ?? f.canonicalFactKey
    : undefined;
}

/** Parse the structured pieces out of a value-bearing finding. Returns null
 *  when the finding carries no recognizable value structure (the row then
 *  renders the classic plain-message hero). Message formats parsed here are
 *  authored by our own checkers — regexes are pinned by their tests. */
export function conflictViewFor(f: UserAuditFinding): ConflictView | null {
  if (isUnavailableFloodPrecedence(f.precedenceViolation)) return null;
  // (1) R-78 provider precedence — fully typed, no prose parsing needed.
  if (f.precedenceViolation) {
    const v = f.precedenceViolation;
    return {
      factLabel: PRECEDENCE_FACT_LABELS[v.fact] ?? v.fact,
      headline: 'report contradicts the verified value',
      cited: [{ value: String(v.reportedValue), source: 'report' }],
      truth: { value: String(v.tier1Value), source: v.tier1Source },
    };
  }

  // Prefer the checker's own fact label from the message prefix ("Nearest
  // IX/peering distance (km) cited with…") — it is more precise than the
  // generic per-key map, which stays as fallback.
  const msgLabel = f.message.match(/^(.{3,60}?)\s+(?:cited with|disagrees across)/)?.[1];
  const label = msgLabel ?? factLabelFor(f);
  if (!label) return null;
  const settledTo = f.message.match(/reconciled to ([^;]+); applies on next regeneration/)?.[1]?.trim();

  // (2) Tier-3 cross-report ("X disagrees across reports. Unreconciled
  //     values: … Source of truth: … Dissenters: …").
  if (f.checkId === 'tier-3') {
    // Values are decimal-heavy (204.9, 2.9) — terminate on the sentence
    // boundary before the next clause, never on the first bare ".", which
    // truncated "204.9, 2.9" to "204" (adversarial review 2026-08-13).
    const values = f.message.match(/Unreconciled values: (.+?)\.(?= Source of truth| Dissenters| —|\s*$)/)?.[1];
    if (!values) return null;
    const truthM = f.message.match(/Source of truth: ([^ ]+) \(([^)]+(?:\([^)]*\))?[^)]*)\)\./);
    const dissenters = f.message.match(/Dissenters: ([^.]+)\./)?.[1];
    const cited = values.split(',').map((v) => ({ value: v.trim() })).filter((c) => c.value.length > 0);
    if (cited.length === 0) return null;
    return {
      factLabel: label,
      headline: 'reports disagree',
      cited,
      ...(truthM ? { truth: { value: truthM[1]!, source: truthM[2]! } } : {}),
      ...(dissenters ? { dissenters: dissenters.split(',').map((d) => d.trim()) } : {}),
      ...(settledTo ? { settledTo } : {}),
    };
  }

  // (3) Tier-2e / R-67 within-report ("cited with N unreconciled value(s) in
  //     same report: … (none match the phase SSOT: …)").
  if (f.checkId === 'tier-2e' || f.checkId === 'R-67') {
    const values = f.message.match(/in same report: ([^(—]+?)(?:\s*\(|\s*—|$)/)?.[1];
    if (!values) return null;
    const ssot = f.message.match(/phase SSOT: ([^)]+)\)/)?.[1];
    const citedWithin = values.split(',').map((v) => ({ value: v.trim() })).filter((c) => c.value.length > 0);
    if (citedWithin.length === 0) return null;
    return {
      factLabel: label,
      headline: 'cited inconsistently within this report',
      cited: citedWithin,
      ...(ssot
        ? { truth: { value: ssot.split(',').map((s) => formatCanonicalValue(s.trim())).join(' · '), source: 'phase plan (SSOT)' } }
        : {}),
      ...(settledTo ? { settledTo } : {}),
    };
  }

  // (4) R-79 cross-report reconciliation ("X disagrees across reports:
  //     feasibility=groundwater, water-resource=surface water."). Values may
  //     contain decimals ("interconnection=1.557134857358192 km"), so pairs
  //     split on ", " — a char-class stopping at "." truncated "1.55…" to "1".
  if (f.checkId === 'R-79') {
    const list = f.message.match(/disagrees across reports: (.+?)\.?\s*$/)?.[1];
    if (!list) return null;
    const pairs = list
      .split(', ')
      .map((seg) => {
        const eq = seg.indexOf('=');
        if (eq <= 0) return null;
        return { source: seg.slice(0, eq).trim(), value: seg.slice(eq + 1).replace(/\.$/, '').trim() };
      })
      .filter((p): p is { source: string; value: string } => p !== null && p.value.length > 0);
    if (pairs.length < 2) return null;
    return {
      factLabel: label,
      headline: 'reports disagree',
      cited: pairs.map((p) => ({ value: p.value, source: p.source })),
      ...(settledTo ? { settledTo } : {}),
    };
  }

  return null;
}

/** Truth-source display names: provenance paths are for the expanded payload,
 *  not the comparison strip ("projectIntent.phases (ultimate cumulative)" →
 *  "phase plan"). Unknown sources pass through unchanged. */
function prettyTruthSource(source: string): string {
  if (/projectIntent\.phases/i.test(source)) return 'phase plan (ultimate)';
  if (/powerRequirementMW/i.test(source)) return 'project intent';
  return source;
}

/** Chip-and-strip renderer for a parsed conflict. Tokens only — rounded-sm
 *  chips, subtle tone surfaces — so the strip sits naturally in the ledger. */
function ConflictSummary({ view, findingId }: { view: ConflictView; findingId: string }) {
  const chip = (text: string, tone: 'cited' | 'truth') => (
    <span
      key={`${tone}-${text}`}
      className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono text-xs"
      style={tone === 'truth'
        ? { backgroundColor: 'var(--success-subtle, var(--surface-2))', color: 'var(--success, var(--text-primary))' }
        : { backgroundColor: 'var(--danger-subtle, var(--surface-2))', color: 'var(--danger, var(--text-primary))' }}
    >
      {formatCanonicalValue(text)}
    </span>
  );
  return (
    <div className="mt-1.5 pl-6" data-testid={`audit-conflict-summary-${findingId}`}>
      <p className="text-sm text-text-primary">
        <span className="font-medium">{view.factLabel}</span>
        <span className="text-text-secondary"> — {view.headline}</span>
      </p>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="inline-flex flex-wrap items-center gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
            {view.cited.length > 1 ? 'Cited' : 'Report cites'}
          </span>
          {view.cited.map((c) => (
            <span key={`${c.source ?? ''}:${c.value}`} className="inline-flex items-center gap-1">
              {c.source && c.source !== 'report' && (
                <span className="font-mono text-[10px] text-text-muted">{c.source}</span>
              )}
              {chip(c.value, 'cited')}
            </span>
          ))}
        </span>
        {view.truth && (
          <span className="inline-flex flex-wrap items-center gap-1">
            <span aria-hidden="true" className="text-text-muted">→</span>
            <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
              Verified · {prettyTruthSource(view.truth.source)}
            </span>
            {chip(view.truth.value, 'truth')}
          </span>
        )}
      </div>
      {view.dissenters && view.dissenters.length > 0 && (
        <p className="mt-1 text-xs text-text-muted">
          Disagreeing reports: {view.dissenters.map((d) => reportTypeLabel(d)).join(', ')}
        </p>
      )}
      {view.settledTo && (
        <p
          className="mt-1 inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs"
          style={{ backgroundColor: 'var(--success-subtle, var(--surface-2))', color: 'var(--success, var(--text-secondary))' }}
          data-testid={`audit-conflict-settled-${findingId}`}
        >
          <Check className="w-3 h-3 shrink-0" aria-hidden="true" />
          Settled → {formatCanonicalValue(view.settledTo)} · rewrites on next regeneration
        </p>
      )}
    </div>
  );
}

/**
 * U6 — small inline value-picker popover for reconciling an internal-number
 * inconsistency. Lets the user promote one of the cited distinct values to
 * canonical and applies it via `onApply`.
 */
function ReconcilePopover({
  finding,
  candidateValues,
  onApply,
  onClose,
}: {
  finding: UserAuditFinding;
  candidateValues: string[];
  onApply: (finding: UserAuditFinding, canonicalValue: string) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<string>(candidateValues[0] ?? '');
  return (
    <div
      className="absolute right-0 z-30 mt-1 w-56 rounded-md border border-border bg-background p-3 text-left shadow-lg"
      role="dialog"
      aria-label="Reconcile to canonical value"
      data-testid={`audit-reconcile-popover-${finding.id}`}
    >
      <p className="text-xs font-semibold text-text-primary mb-2">Pick canonical value</p>
      <div className="space-y-1 max-h-40 overflow-y-auto">
        {candidateValues.map((value) => (
          <label
            key={value}
            className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer"
          >
            <input
              type="radio"
              name={`reconcile-${finding.id}`}
              value={value}
              checked={selected === value}
              onChange={() => setSelected(value)}
              data-testid={`audit-reconcile-option-${finding.id}-${value}`}
            />
            <span className="font-mono">{formatCanonicalValue(value)}</span>
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button
          type="button"
          className="text-xs text-text-secondary hover:text-text-primary"
          onClick={onClose}
          data-testid={`audit-reconcile-cancel-${finding.id}`}
        >
          Cancel
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs text-white hover:bg-primary/90 disabled:opacity-50"
          disabled={!selected}
          onClick={() => onApply(finding, selected)}
          data-testid={`audit-reconcile-apply-${finding.id}`}
        >
          <Check className="w-3 h-3" /> Apply
        </button>
      </div>
    </div>
  );
}

/**
 * #112 — render ONE finding as an engineering-log entry (NOT a rigid table
 * cell). Layout:
 *   • left severity accent rule (border-l-2) colored by severityTone()
 *   • top line: severity pill + mono metadata (Tier + FULL report label) and
 *     a right-aligned action control
 *   • the DETAIL/message is the hero — full text, WRAPPING, no truncation
 *   • expand chevron reveals the extra payload (context/intent/SED/precedence),
 *     where the raw internal check-id is surfaced (and only there)
 */
function FindingLogRow({
  finding: f,
  stage,
  expanded,
  hasPayload,
  anchorHref,
  isValidated,
  fix,
  secondaryFix,
  canSyncIntent,
  onToggleExpand,
  reconcilingId,
  onStartReconcile,
  onCancelReconcile,
  onApplyReconcile,
  onValidate,
  onRegenerate,
  onFocusQuality,
  onAmendSed,
  onSyncIntent,
  onEscalate,
  onDismiss,
  regeneratingId,
}: {
  finding: UserAuditFinding;
  stage: 1 | 2;
  expanded: boolean;
  hasPayload: boolean;
  /** P3 — resolved report-reader deep-link (#section-…) for this finding's
   *  paragraph anchor; null when the finding carries no resolvable anchor. */
  anchorHref: string | null;
  /** P3 — true once the user has affirmed/validated this finding's value. */
  isValidated: boolean;
  /** The ONE primary fix derived by findingFixResolver. */
  fix: FindingFix;
  /** Optional secondary fix (R-78 "dispute verified value" SED amendment). */
  secondaryFix: FindingFix | null;
  /** R-65 — a typed sedSyncValue exists, enabling one-click intent sync. */
  canSyncIntent: boolean;
  onToggleExpand: (id: string) => void;
  reconcilingId: string | null;
  onStartReconcile: (id: string) => void;
  onCancelReconcile: () => void;
  onApplyReconcile: (finding: UserAuditFinding, canonicalValue: string) => void;
  onValidate: (finding: UserAuditFinding) => void;
  onRegenerate: (reportId: string, findingId: string) => void;
  onFocusQuality: (reportId: string) => void;
  onAmendSed: (finding: UserAuditFinding, fix: Extract<FindingFix, { kind: 'amend-sed' }>) => void;
  onSyncIntent: (finding: UserAuditFinding) => void;
  /** File the finding as a platform bug (B2/R-82 escalation). */
  onEscalate: (finding: UserAuditFinding) => void;
  /** Phase-3 — park the finding (no correctness assertion; undoable). */
  onDismiss: (finding: UserAuditFinding) => void;
  /** Finding id whose regenerate is in flight (disables its button). */
  regeneratingId: string | null;
}) {
  const tone = severityTone(f.severity);
  const titleText = fix.label || undefined;
  // A value-bearing inconsistency offers a CHANGE control (the reconcile
  // value-picker). Both CHANGE and VALIDATE are surfaced for these findings.
  const canChange = fix.kind === 'reconcile';
  const isBlocking = f.severity === 'block';

  // The ONE primary action, right-aligned on the metadata line. Every check
  // family resolves to an executable control (findingFixResolver) — the old
  // "Regenerate affected section" label that merely opened the report is gone.
  const action = (() => {
    switch (fix.kind) {
      // (1) Value conflict → Reconcile value-picker.
      case 'reconcile':
        return (
          <div className="relative">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
              title={titleText}
              aria-label={fix.label}
              aria-haspopup="dialog"
              aria-expanded={reconcilingId === f.id}
              onClick={() =>
                reconcilingId === f.id ? onCancelReconcile() : onStartReconcile(f.id)
              }
              data-testid={`audit-action-reconcile-${f.id}`}
            >
              <Wrench className="w-3.5 h-3.5 shrink-0" />
              <span>{fix.label}</span>
            </button>
            {reconcilingId === f.id && (
              <ReconcilePopover
                finding={f}
                candidateValues={fix.candidateValues}
                onApply={onApplyReconcile}
                onClose={onCancelReconcile}
              />
            )}
          </div>
        );

      // (2) Content / precedence issue — resolved by regenerating the report.
      // Regeneration is a REPORT-level action (one run applies every pending
      // fix), so the button lives on the group header; a per-row button here
      // invited one regeneration per finding — N slow, costly runs where one
      // suffices. The row shows a passive marker instead.
      case 'regenerate':
        return (
          <span
            className="inline-flex items-center gap-1 text-xs text-text-muted"
            title="Resolved when the report is regenerated — settle the other findings first, then regenerate once from the report header above"
            data-testid={`audit-action-regenerate-${f.id}`}
          >
            <RefreshCw className={`w-3.5 h-3.5 shrink-0 ${regeneratingId === fix.reportId ? 'animate-spin' : ''}`} />
            <span>{regeneratingId === fix.reportId ? 'Regenerating…' : 'Fixed on regeneration'}</span>
          </span>
        );

      // (3) Sanity/quality finding → the report's apply/ignore review flow.
      case 'apply-directive':
        return (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
            title={titleText}
            aria-label={fix.label}
            onClick={() => onFocusQuality(fix.reportId)}
            data-testid={`audit-action-directive-${f.id}`}
          >
            <Wrench className="w-3.5 h-3.5 shrink-0" />
            <span>{fix.label}</span>
          </button>
        );

      // (4) R-65 → the Intent surface owns the fix.
      case 'edit-intent':
        return (
          <span className="inline-flex items-center gap-3">
            {canSyncIntent && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                title="Adopt the SED's value into Project Intent"
                aria-label="Use SED value"
                onClick={() => onSyncIntent(f)}
                data-testid={`audit-action-sync-intent-${f.id}`}
              >
                <CheckCheck className="w-3.5 h-3.5 shrink-0" />
                <span>Use SED value</span>
              </button>
            )}
            <a
              href={fix.href}
              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
              title={titleText}
              aria-label={fix.label}
              data-testid={`audit-action-link-${f.id}`}
            >
              <span>{fix.label}</span>
              <ArrowUpRight className="w-3.5 h-3.5 shrink-0" />
            </a>
          </span>
        );

      // (5) Navigation fallback (source doc, financial tab…).
      case 'open-link':
        return (
          <a
            href={fix.href}
            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
            title={titleText}
            aria-label={fix.label}
            data-testid={`audit-action-link-${f.id}`}
          >
            <span>{fix.label}</span>
            <ArrowUpRight className="w-3.5 h-3.5 shrink-0" />
          </a>
        );

      case 'view-detail':
        return (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
            title="View detail"
            aria-label="View detail"
            onClick={() => onToggleExpand(f.id)}
            data-testid={`audit-action-view-${f.id}`}
          >
            <span>View detail</span>
            <ArrowUpRight className="w-3.5 h-3.5 shrink-0" />
          </button>
        );

      default:
        return <span className="text-text-muted text-xs">—</span>;
    }
  })();

  // Structured comparison strip for value-bearing findings (null → classic
  // plain-message hero). Parsed once per row render.
  const conflictView = conflictViewFor(f);

  return (
    <div
      className="rounded-md border-l-2 pl-3 pr-3 py-2.5 hover:bg-muted/40 transition-colors"
      style={{ borderLeftColor: tone.dot }}
      data-testid={`audit-finding-row-${f.id}`}
      data-validated={isValidated ? 'true' : undefined}
    >
      {/* Top line: chevron + severity pill + mono metadata, action right-aligned */}
      <div className="flex items-start gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
          {hasPayload ? (
            <button
              type="button"
              className="text-text-secondary hover:text-text-primary mt-0.5 shrink-0"
              onClick={() => onToggleExpand(f.id)}
              aria-expanded={expanded}
              data-testid={`audit-expand-${f.id}`}
            >
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : (
            <span className="w-4 h-4 shrink-0" aria-hidden="true" />
          )}
          <span
            className="inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs font-medium capitalize shrink-0"
            style={{ backgroundColor: tone.bg, color: tone.fg }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
              style={{ backgroundColor: tone.dot }}
              aria-hidden="true"
            />
            {f.severity}
          </span>
          {/* P3 — BLOCK-severity findings are explicitly flagged as must-resolve
              (a blocking conflict holds back dispatch-readiness). */}
          {isBlocking && (
            <span
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide shrink-0"
              style={{ backgroundColor: 'var(--danger-subtle)', color: 'var(--danger)' }}
              title={stage === 1
                ? 'Resolve this source or data conflict before relying on conditional screening findings. Engineering validation and final GO requirements remain separate.'
                : 'This is a blocking conflict — it must be resolved before this site’s reports are dispatch-ready.'}
              data-testid={`audit-blocking-badge-${f.id}`}
            >
              <Lock className="w-3 h-3 shrink-0" aria-hidden="true" />
              Must resolve
            </span>
          )}
          {/* P3 — once affirmed, surface a "validated" confirmation chip. */}
          {isValidated && (
            <span
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium shrink-0"
              style={{ backgroundColor: 'var(--success-subtle, var(--surface-2))', color: 'var(--success, var(--text-secondary))' }}
              data-testid={`audit-validated-badge-${f.id}`}
            >
              <Check className="w-3 h-3 shrink-0" aria-hidden="true" />
              Validated
            </span>
          )}
          <span className="inline-flex items-center gap-2 font-mono text-xs text-text-muted min-w-0">
            <span className="rounded bg-surface-2 px-1.5 py-0.5 whitespace-nowrap">{f.tier}</span>
            <span className="truncate" title={f.reportType ?? undefined}>
              {reportTypeLabel(f.reportType)}
            </span>
          </span>
        </div>
        <div className="shrink-0 text-right">{action}</div>
      </div>

      {/* Hero: value-bearing findings render the structured comparison strip
          (fact label · cited vs verified chips · settled badge); everything
          else keeps the classic full-text message. The raw message stays
          verbatim in the expanded payload either way. */}
      {conflictView ? (
        <ConflictSummary view={conflictView} findingId={f.id} />
      ) : (
        <p className="mt-1.5 pl-6 text-sm text-text-primary break-words whitespace-pre-wrap">
          {f.message}
        </p>
      )}

      {/* P3 — RESOLUTION CONTROL ROW: deep-link to the offending paragraph plus
          the Change / Validate affordances. The deep-link appears for any
          finding carrying a resolvable paragraph anchor; Validate appears for
          every finding (affirm/accept the value, clearing it); Change reuses
          the reconcile value-picker (shown above) for value-bearing findings.
          Validate is always offered, so this control row always renders. */}
      <div className="mt-1.5 pl-6 flex flex-wrap items-center gap-3" data-testid={`audit-resolution-controls-${f.id}`}>
          {anchorHref && (
            <a
              href={anchorHref}
              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
              title="Open the report and scroll to the offending paragraph"
              aria-label="Go to paragraph"
              data-testid={`audit-goto-paragraph-${f.id}`}
            >
              <MapPin className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              <span>Go to paragraph</span>
            </a>
          )}
          {canChange && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
              title="Correct the value"
              aria-label="Change value"
              aria-haspopup="dialog"
              aria-expanded={reconcilingId === f.id}
              onClick={() =>
                reconcilingId === f.id ? onCancelReconcile() : onStartReconcile(f.id)
              }
              data-testid={`audit-change-${f.id}`}
            >
              <Wrench className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              <span>Change</span>
            </button>
          )}
          {secondaryFix?.kind === 'amend-sed' && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
              title="The verified provider value is wrong for this site — correct it at the source (SED) with a rationale"
              aria-label={secondaryFix.label}
              onClick={() => onAmendSed(f, secondaryFix)}
              data-testid={`audit-dispute-tier1-${f.id}`}
            >
              <Wrench className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              <span>{secondaryFix.label}</span>
            </button>
          )}
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-success hover:opacity-80 disabled:opacity-50"
            title="Confirm this value is correct and clear the finding"
            aria-label="Validate value"
            disabled={isValidated}
            onClick={() => onValidate(f)}
            data-testid={`audit-validate-${f.id}`}
          >
            <ThumbsUp className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            <span>{isValidated ? 'Validated' : 'Validate'}</span>
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text-primary"
            title="Park this finding without deciding it's correct — it stops counting against trust and can be restored any time"
            aria-label="Dismiss finding"
            onClick={() => onDismiss(f)}
            data-testid={`audit-dismiss-${f.id}`}
          >
            <X className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            <span>Dismiss</span>
          </button>
          {/* B2/R-82 — escalation is reachable again (the only caller used to
              be the dead AuditTabPanel): file the finding as a platform bug. */}
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text-primary"
            title="This looks like a platform problem — send it to Datacrate as a bug report"
            aria-label="Report to Datacrate"
            onClick={() => onEscalate(f)}
            data-testid={`audit-escalate-${f.id}`}
          >
            <ArrowUpRight className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            <span>Report to Datacrate</span>
          </button>
        </div>

      {/* P3 — anchor excerpt: the offending source snippet, inline for context. */}
      {f.anchor?.excerpt && (
        <p
          className="mt-1 pl-6 text-xs italic text-text-secondary break-words"
          data-testid={`audit-anchor-excerpt-${f.id}`}
        >
          “{f.anchor.excerpt}”
        </p>
      )}

      {/* Expandable payload — and the ONLY place the raw internal check-id shows. */}
      {expanded && hasPayload && (
        <div className="mt-2 ml-6 rounded-md bg-muted/30 px-3 py-2 text-xs text-text-secondary whitespace-pre-wrap">
          {/* When the hero rendered the structured strip, keep the checker's
              verbatim message here — fidelity is never lost to formatting. */}
          {conflictView && (
            <p className="mb-1 font-mono text-[11px] text-text-muted">{f.message}</p>
          )}
          {f.detail && <p className="mb-1">{f.detail}</p>}
          {f.context && <pre className="text-[11px] overflow-x-auto">{f.context}</pre>}
          {f.intentValue && (
            <p><strong>Intent:</strong> {f.intentValue} · <strong>SED:</strong> {f.sedValue}</p>
          )}
          {f.sedExcerpt && <p className="italic mt-1">{f.sedExcerpt}</p>}
          {f.precedenceViolation && (
            <div className="mt-1 space-y-0.5">
              <p>
                <strong>{f.precedenceViolation.fact}</strong> — report value{' '}
                <span className="font-mono">{String(f.precedenceViolation.reportedValue)}</span>
                {isUnavailableFloodPrecedence(f.precedenceViolation) ? ' — provider result unavailable: ' : ' vs verified '}
                <span className="font-mono">{String(f.precedenceViolation.tier1Value)}</span>
                {' '}(source: {f.precedenceViolation.tier1Source})
              </p>
              <p className="text-text-muted">
                Suggested fix: {f.precedenceViolation.suggestedFix}
              </p>
              {f.precedenceViolation.excerpt && (
                <p className="italic">“{f.precedenceViolation.excerpt}”</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * PER-REPORT TRIAGE LEDGER (#112 rows, regrouped).
 *
 * The flat 50-row stack made per-report fixing impossible: findings from six
 * reports interleaved, with no filters and a silent global cap. This groups
 * the ledger by report — "Site-wide checks" first, then one collapsible group
 * per report ordered by blocking count — with a severity/report/search filter
 * bar, a per-group cap (+ "Show all"), and a "Fix next" helper that jumps to
 * the group's first blocking finding. Rows are unchanged FindingLogRow
 * entries, so every fix affordance keeps its testid.
 *
 * `initialReportId` (from `?report=<id>` — the same deep-link the blocked-
 * publish dialog and the reader's banner emit) preselects the report filter,
 * landing the user directly on that report's fix queue.
 */
function AuditFindingsLedger({
  findings,
  stage,
  expandedIds,
  validatedIds,
  siteId,
  sedId,
  siteReports,
  canSyncIntentFor,
  onToggleExpand,
  reconcilingId,
  onStartReconcile,
  onCancelReconcile,
  onApplyReconcile,
  onValidate,
  onRegenerate,
  onFocusQuality,
  onAmendSed,
  onSyncIntent,
  onEscalate,
  onDismiss,
  regeneratingId,
  initialReportId,
  onViewReport,
}: {
  findings: UserAuditFinding[];
  stage: 1 | 2;
  expandedIds: Set<string>;
  validatedIds: Set<string>;
  siteId: string;
  sedId?: string;
  siteReports: SiteRagReport[];
  canSyncIntentFor: (finding: UserAuditFinding) => boolean;
  onToggleExpand: (id: string) => void;
  reconcilingId: string | null;
  onStartReconcile: (id: string) => void;
  onCancelReconcile: () => void;
  onApplyReconcile: (finding: UserAuditFinding, canonicalValue: string) => void;
  onValidate: (finding: UserAuditFinding) => void;
  onRegenerate: (reportId: string, findingId: string) => void;
  onFocusQuality: (reportId: string) => void;
  onAmendSed: (finding: UserAuditFinding, fix: Extract<FindingFix, { kind: 'amend-sed' }>) => void;
  onSyncIntent: (finding: UserAuditFinding) => void;
  onEscalate: (finding: UserAuditFinding) => void;
  onDismiss: (finding: UserAuditFinding) => void;
  regeneratingId: string | null;
  initialReportId?: string | null;
  onViewReport?: (reportId: string) => void;
}) {
  type Severity = UserAuditFinding['severity'];
  const SITE_KEY = 'site';
  const [severityFilter, setSeverityFilter] = useState<Set<Severity>>(() => new Set());
  const [reportFilter, setReportFilter] = useState<string>(() =>
    initialReportId && findings.some((f) => f.reportId === initialReportId) ? initialReportId : 'all',
  );
  const [search, setSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  const [uncappedGroups, setUncappedGroups] = useState<Set<string>>(() => new Set());

  const toggleSeverity = (sev: Severity) =>
    setSeverityFilter((prev) => {
      const next = new Set(prev);
      if (next.has(sev)) next.delete(sev);
      else next.add(sev);
      return next;
    });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return findings.filter((f) => {
      if (severityFilter.size > 0 && !severityFilter.has(f.severity)) return false;
      // SITE-WIDE FINDINGS ARE NEVER FILTERED OUT BY A REPORT SELECTION
      // (2026-08-14). Findings with no reportId (R-71 SED coverage, R-79
      // cross-report conflicts) belong to the SITE, so a report filter could
      // never match them — yet the header keeps counting them. Deep-linking
      // to a report therefore showed "1 blocking" above a list containing no
      // blocking row at all. They are context for every report; keep them.
      const isSiteWide = !f.reportId;
      if (reportFilter !== 'all' && !isSiteWide && f.reportId !== reportFilter) return false;
      if (q && !`${f.message} ${f.detail ?? ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [findings, severityFilter, reportFilter, search]);

  /** Group by report — SITE first, then by blocking count, then recency. */
  const groups = useMemo(() => {
    const byKey = new Map<string, UserAuditFinding[]>();
    for (const f of filtered) {
      const key = f.reportId ?? SITE_KEY;
      const bucket = byKey.get(key);
      if (bucket) bucket.push(f);
      else byKey.set(key, [f]);
    }
    const entries = [...byKey.entries()].map(([key, groupFindings]) => {
      const report = key === SITE_KEY ? undefined : siteReports.find((r) => r.id === key);
      const blocks = groupFindings.filter((f) => f.severity === 'block').length;
      const warns = groupFindings.filter((f) => f.severity === 'warn').length;
      return { key, report, groupFindings, blocks, warns };
    });
    entries.sort((a, b) => {
      if ((a.key === SITE_KEY) !== (b.key === SITE_KEY)) return a.key === SITE_KEY ? -1 : 1;
      if (a.blocks !== b.blocks) return b.blocks - a.blocks;
      const at = a.report ? new Date(a.report.createdAt).getTime() : 0;
      const bt = b.report ? new Date(b.report.createdAt).getTime() : 0;
      return bt - at;
    });
    return entries;
  }, [filtered, siteReports]);

  /** Distinct report options for the filter select (from ALL findings). */
  const reportOptions = useMemo(() => {
    const keys = new Set<string>(findings.map((f) => f.reportId ?? SITE_KEY));
    return [...keys].map((key) => {
      const report = key === SITE_KEY ? undefined : siteReports.find((r) => r.id === key);
      // The report object can be absent (older revision, still-loading list) —
      // the findings themselves carry reportType, so never show a raw id.
      const typeOf = findings.find((f) => (f.reportId ?? SITE_KEY) === key)?.reportType;
      return {
        key,
        label:
          key === SITE_KEY
            ? 'Site-wide checks'
            : report
              ? `${reportTypeLabel(report.reportType)} · ${new Date(report.createdAt).toLocaleDateString()}`
              : typeOf
                ? reportTypeLabel(typeOf)
                : key,
      };
    });
  }, [findings, siteReports]);

  /** "Fix next" — expand + scroll to the group's first blocking finding. */
  const fixNext = (groupFindings: UserAuditFinding[]) => {
    const target = groupFindings.find((f) => f.severity === 'block') ?? groupFindings[0];
    if (!target) return;
    const hasPayload = !!(target.detail || target.context || target.intentValue || target.precedenceViolation);
    if (hasPayload && !expandedIds.has(target.id)) onToggleExpand(target.id);
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-testid="audit-finding-row-${CSS.escape(target.id)}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const severityChip = (sev: Severity, label: string) => (
    <button
      key={sev}
      type="button"
      onClick={() => toggleSeverity(sev)}
      aria-pressed={severityFilter.has(sev)}
      className={`min-h-10 rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        severityFilter.has(sev)
          ? 'selected-accent'
          : 'border-border text-text-muted hover:text-text-primary'
      }`}
      data-testid={`audit-filter-severity-${sev}`}
    >
      {label}
    </button>
  );

  return (
    <div data-testid="audit-findings-table">
      {/* ── Filter bar — severity chips · report select · search ── */}
      <div
        className="mb-2 flex flex-wrap items-center gap-2"
        data-testid="audit-ledger-filters"
      >
        <div className="flex items-center gap-1.5">
          {severityChip('block', 'Blocking')}
          {severityChip('warn', 'Warnings')}
          {severityChip('info', 'Info')}
        </div>
        <select
          className="min-h-10 rounded-md border border-border bg-background px-3 py-2 text-sm text-text-primary"
          value={reportFilter}
          onChange={(e) => setReportFilter(e.target.value)}
          aria-label="Filter findings by report"
          data-testid="audit-filter-report"
        >
          <option value="all">All reports</option>
          {reportOptions.map((o) => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
        <input
          type="search"
          className="min-h-10 min-w-32 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-text-primary placeholder:text-text-muted"
          placeholder="Search findings…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search findings"
          data-testid="audit-filter-search"
        />
        {filtered.length !== findings.length && (
          <span className="text-xs text-text-muted" data-testid="audit-filter-count">
            {filtered.length} of {findings.length}
          </span>
        )}
        {(severityFilter.size > 0 || reportFilter !== 'all' || search.length > 0) && (
          <Button type="button" variant="ghost" size="sm" onClick={() => {
            setSeverityFilter(new Set());
            setReportFilter('all');
            setSearch('');
          }}>Clear filters</Button>
        )}
      </div>

      {/* ── Report groups ── */}
      <div
        className="space-y-2 overflow-y-auto overflow-x-hidden"
        style={{ maxHeight: TABLE_MAX_HEIGHT }}
      >
        {groups.length === 0 && (
          <p className="px-1 py-2 text-xs text-text-muted" data-testid="audit-ledger-empty">
            No findings match the current filters.
          </p>
        )}
        {groups.map(({ key, report, groupFindings, blocks, warns }) => {
          const isCollapsed = collapsedGroups.has(key);
          const cap = uncappedGroups.has(key) ? groupFindings.length : GROUP_CAP;
          const visible = groupFindings.slice(0, cap);
          const overflow = groupFindings.length - visible.length;
          return (
            <section
              key={key}
              className="rounded-lg border border-border"
              data-testid={`audit-report-group-${key}`}
            >
              <div className="flex flex-wrap items-center gap-2 px-2.5 py-2">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-xs font-semibold text-text-primary hover:text-primary"
                  onClick={() => toggleGroup(key)}
                  aria-expanded={!isCollapsed}
                  data-testid={`audit-group-toggle-${key}`}
                >
                  {isCollapsed ? <ChevronRight className="h-3.5 w-3.5 shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0" />}
                  {key === SITE_KEY ? (
                    <span className="truncate">Site-wide checks</span>
                  ) : (
                    <span className="truncate">
                      {/* Findings carry reportType — never show a raw report id
                          when the report object is absent from siteReports. */}
                      {report
                        ? reportTypeLabel(report.reportType)
                        : reportTypeLabel(groupFindings[0]?.reportType)}
                      {report && (
                        <span className="ml-1.5 font-mono text-[10px] font-normal text-text-muted">
                          {new Date(report.createdAt).toLocaleDateString()}
                        </span>
                      )}
                    </span>
                  )}
                </button>
                {blocks > 0 && (
                  <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ backgroundColor: 'var(--danger-subtle)', color: 'var(--danger)' }}>
                    {blocks} blocking
                  </span>
                )}
                {warns > 0 && (
                  <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ backgroundColor: 'var(--warning-subtle, var(--surface-2))', color: 'var(--warning)' }}>
                    {warns} warn
                  </span>
                )}
                {blocks > 0 && !isCollapsed && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                    title="Jump to this report's first blocking finding"
                    onClick={() => fixNext(groupFindings)}
                    data-testid={`audit-fix-next-${key}`}
                  >
                    <Wrench className="h-3.5 w-3.5" />
                    Fix next
                  </button>
                )}
                {/* ONE regenerate per report: fixes (reconciles, verified
                    values, depth) all land in a single run — surfacing this
                    per finding invited N runs where one suffices. Offered
                    once the group has anything a regeneration would fix. */}
                {key !== SITE_KEY && groupFindings.some((finding) =>
                  (finding.severity === 'block' || finding.severity === 'warn')
                  && !isUnavailableFloodPrecedence(finding.precedenceViolation)) && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 disabled:opacity-50"
                    title="Regenerate this report once — applies every recorded decision and verified value in a single run"
                    disabled={regeneratingId === key}
                    onClick={() => onRegenerate(key, key)}
                    data-testid={`audit-group-regenerate-${key}`}
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${regeneratingId === key ? 'animate-spin' : ''}`} />
                    {regeneratingId === key ? 'Regenerating…' : 'Regenerate report'}
                  </button>
                )}
                {key !== SITE_KEY && onViewReport && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-primary"
                    onClick={() => onViewReport(key)}
                    data-testid={`audit-group-open-${key}`}
                  >
                    Open report
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {/* hidden-not-unmounted so row testids stay resolvable */}
              <div className={isCollapsed ? 'hidden' : 'space-y-1.5 border-t border-border p-2'}>
                {visible.map((f) => {
                  const expanded = expandedIds.has(f.id);
                  const hasPayload = !!(f.detail || f.context || f.intentValue || f.precedenceViolation);
                  const anchorHref = buildAnchorDeepLink(f, siteId, siteReports);
                  const fixCtx: FindingFixContext = { siteId, siteReports, sedId, hasPayload };
                  return (
                    <FindingLogRow
                      key={f.id}
                      finding={f}
                      stage={stage}
                      expanded={expanded}
                      hasPayload={hasPayload}
                      anchorHref={anchorHref}
                      isValidated={validatedIds.has(f.id)}
                      fix={resolveFindingFix(f, fixCtx)}
                      secondaryFix={resolveSecondaryFix(f, fixCtx)}
                      canSyncIntent={canSyncIntentFor(f)}
                      onToggleExpand={onToggleExpand}
                      reconcilingId={reconcilingId}
                      onStartReconcile={onStartReconcile}
                      onCancelReconcile={onCancelReconcile}
                      onApplyReconcile={onApplyReconcile}
                      onValidate={onValidate}
                      onRegenerate={onRegenerate}
                      onFocusQuality={onFocusQuality}
                      onAmendSed={onAmendSed}
                      onSyncIntent={onSyncIntent}
                      onEscalate={onEscalate}
                      onDismiss={onDismiss}
                      regeneratingId={regeneratingId}
                    />
                  );
                })}
                {overflow > 0 && (
                  <button
                    type="button"
                    className="px-2 py-1.5 text-xs text-primary hover:text-primary/80"
                    onClick={() =>
                      setUncappedGroups((prev) => new Set(prev).add(key))
                    }
                    data-testid={`audit-group-show-all-${key}`}
                  >
                    Show all {groupFindings.length} findings (+{overflow} more)
                  </button>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

/* ReconciliationPane retired — its three tabs rendered R-65/R-78/R-79
   payloads as inert text that duplicated the findings ledger; the unique
   fields (recommendedAction, remediationHint) already ride on the findings
   themselves (detail / action). One surface, one grammar. */

function DistributionSanitySection({ siteId, reports }: { siteId: string; reports: SiteRagReport[] }) {
  const allReports = useStore((s) => s.siteRag.reports);
  const siteReports = useMemo(
    () => reports ?? allReports.filter((r) => r.siteId === siteId),
    [allReports, siteId, reports],
  );
  const rows = useMemo(() => {
    const out: Array<{
      reportId: string;
      reportType: string;
      finding: NonNullable<NonNullable<SiteRagReport['qaFindings']>['distribution']>[number];
    }> = [];
    for (const report of siteReports) {
      for (const finding of report.qaFindings?.distribution ?? []) {
        out.push({
          reportId: report.id,
          reportType: report.reportType ?? 'feasibility',
          finding,
        });
      }
    }
    return out;
  }, [siteReports]);

  const insufficient = useMemo(
    () => siteReports.filter((r) => r.qaFindings?.distributionCheckMeta?.insufficientCorpus),
    [siteReports],
  );

  if (rows.length === 0 && insufficient.length === 0) return null;

  return (
    <section className="space-y-3" data-testid="audit-distribution-sanity">
      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary">
        Outlier checks
      </h3>
      {insufficient.length > 0 && (
        <p className="text-xs text-warning">
          Corpus sample below minimum for {insufficient.length} report(s) — percentile checks skipped; hard word-count floors still apply.
        </p>
      )}
      {rows.length === 0 ? (
        <p className="text-sm text-text-secondary">No distribution outliers detected.</p>
      ) : (
        <div className="border border-border rounded-lg overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-left text-xs uppercase tracking-wider text-text-secondary">
                <th className="px-3 py-2">Report</th>
                <th className="px-3 py-2">Stat</th>
                <th className="px-3 py-2">Observed</th>
                <th className="px-3 py-2">p10–p90</th>
                <th className="px-3 py-2">Severity</th>
                <th className="px-3 py-2">Suggestion</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ reportId, reportType, finding }) => (
                <tr key={`${reportId}-${finding.stat}`} className="border-b border-border/60">
                  <td className="px-3 py-2 text-xs font-mono">{reportType}</td>
                  <td className="px-3 py-2">{finding.statLabel}</td>
                  <td className="px-3 py-2 tabular-nums">{finding.observed}</td>
                  <td className="px-3 py-2 tabular-nums text-text-secondary">{finding.p10}–{finding.p90}</td>
                  <td className={`px-3 py-2 capitalize ${severityClass(finding.severity)}`}>{finding.severity}</td>
                  <td className="px-3 py-2 text-xs text-text-secondary">{finding.suggestion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function DocProvenanceSection({ site, reports }: { site: SiteEntity; reports: SiteRagReport[] }) {
  const siteReports = reports;
  const [resolveDocId, setResolveDocId] = useState<string>('');
  const docs = site.documents ?? [];

  const rows = useMemo(() => {
    const out: Array<{
      reportId: string;
      reportType: string;
      finding: NonNullable<NonNullable<SiteRagReport['qaFindings']>['docProvenanceFindings']>[number];
    }> = [];
    for (const report of siteReports) {
      for (const finding of report.qaFindings?.docProvenanceFindings ?? []) {
        out.push({
          reportId: report.id,
          reportType: report.reportType ?? 'feasibility',
          finding,
        });
      }
    }
    return out;
  }, [siteReports]);

  if (rows.length === 0) return null;

  return (
    <section className="space-y-3" data-testid="audit-doc-provenance">
      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary">
        Provenance detail
      </h3>
      <div className="flex items-center gap-2 text-xs">
        <label htmlFor="doc-provenance-resolve" className="text-text-secondary shrink-0">
          Resolve to doc…
        </label>
        <select
          id="doc-provenance-resolve"
          className="border border-border rounded px-2 py-1 text-xs bg-background max-w-xs"
          value={resolveDocId}
          onChange={(e) => setResolveDocId(e.target.value)}
          data-testid="doc-provenance-resolve-select"
        >
          <option value="">Select document</option>
          {docs.map((d) => (
            <option key={d.id} value={d.id}>{resolveSiteDocumentFileName(d, d.id)}</option>
          ))}
        </select>
        {resolveDocId && (
          <a
            href={`/docs/${encodeURIComponent(resolveDocId)}`}
            className="text-primary underline"
            data-testid="doc-provenance-open-viewer"
          >
            Open viewer
          </a>
        )}
      </div>
      <div className="border border-border rounded-lg overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-left text-xs uppercase tracking-wider text-text-secondary">
              <th className="px-3 py-2">Report</th>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Severity</th>
              <th className="px-3 py-2">Line</th>
              <th className="px-3 py-2">Message</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ reportId, reportType, finding }) => (
              <tr key={`${reportId}-${finding.lineNumber}-${finding.kind}`} className="border-b border-border/60">
                <td className="px-3 py-2 text-xs font-mono">{reportType}</td>
                <td className="px-3 py-2 font-mono text-xs">{finding.kind}</td>
                <td className={`px-3 py-2 capitalize ${severityClass(finding.severity)}`}>{finding.severity}</td>
                <td className="px-3 py-2 tabular-nums">{finding.lineNumber}</td>
                <td className="px-3 py-2 text-xs text-text-secondary">{finding.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function QuoteCitationIntegritySection({ site }: { site: SiteEntity }) {
  const sourceDocuments = useMemo(() => {
    const ids = new Set((site.supplyChainPlan?.quotes ?? []).map(quote => quote.provenance?.sourceDocId ?? quote.documentId));
    return (site.documents ?? []).filter(document => ids.has(document.id));
  }, [site.documents, site.supplyChainPlan]);
  const [bodyRead, setBodyRead] = useState<{ source: readonly SiteDocument[]; documents?: SiteDocument[]; error?: string } | null>(null);
  const needsRead = sourceDocuments.some(documentNeedsBodyHydration);
  useEffect(() => {
    if (!needsRead) return;
    let cancelled = false;
    void hydrateDocumentBodiesForEvidence(sourceDocuments).then(documents => {
      if (!cancelled) setBodyRead({ source: sourceDocuments, documents });
    }).catch((error: unknown) => {
      if (!cancelled) setBodyRead({ source: sourceDocuments, error: error instanceof Error ? error.message : 'Full quote source text is unavailable.' });
    });
    return () => { cancelled = true; };
  }, [sourceDocuments, needsRead]);
  const currentRead = bodyRead?.source === sourceDocuments ? bodyRead : null;
  const documents = needsRead ? currentRead?.documents : sourceDocuments;
  const rows = useMemo(() => {
    const plan = site.supplyChainPlan;
    if (!plan || !documents) return [];
    const docs = new Map(documents.map(d => [d.id, documentBodyText(d)]));
    const out: Array<{
      quoteId: string;
      supplierId: string;
      field: string;
      kind: string;
      severity: 'block' | 'warn' | 'info';
    }> = [];
    for (const quote of plan.quotes) {
      const sourceDocId = quote.provenance?.sourceDocId ?? quote.documentId;
      const violations = verifyQuoteCitations(quote, sourceDocId ? docs.get(sourceDocId) : null);
      for (const v of violations) {
        out.push({
          quoteId: quote.id,
          supplierId: quote.supplierId,
          field: v.field,
          kind: v.kind,
          severity: v.severity,
        });
      }
    }
    return out;
  }, [documents, site.supplyChainPlan]);

  if (needsRead && !documents) return <section data-testid="audit-quote-source-status" className="space-y-2">
    <h3 className="type-section text-text-secondary">Quote citation integrity</h3>
    <p role="status" className="text-xs text-text-secondary">{currentRead?.error
      ? `Quote verification is incomplete. ${currentRead.error}` : 'Loading complete quote sources before checking citations…'}</p>
  </section>;

  if (rows.length === 0) return null;

  return (
    <section className="space-y-3" data-testid="audit-quote-citation-integrity">
      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary">
        Quote citation integrity
      </h3>
      <div className="border border-border rounded-lg overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-left text-xs uppercase tracking-wider text-text-secondary">
              <th className="px-3 py-2">Quote</th>
              <th className="px-3 py-2">Supplier</th>
              <th className="px-3 py-2">Field</th>
              <th className="px-3 py-2">Violation</th>
              <th className="px-3 py-2">Severity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.quoteId}-${r.field}-${r.kind}-${i}`} className="border-b border-border/60">
                <td className="px-3 py-2 font-mono text-xs">{r.quoteId}</td>
                <td className="px-3 py-2 text-xs">{r.supplierId}</td>
                <td className="px-3 py-2">{r.field}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.kind}</td>
                <td className={`px-3 py-2 capitalize ${severityClass(r.severity)}`}>{r.severity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}


function ScenarioSensitivitySection({ findings }: { findings: UserAuditFinding[] }) {
  const scenarioFindings = findings.filter((f) => f.checkId === 'R-FA-SCEN');
  if (scenarioFindings.length === 0) return null;

  return (
    <section className="space-y-3" data-testid="audit-scenario-sensitivity">
      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary">
        Scenario sensitivity
      </h3>
      <div className="space-y-2">
        {scenarioFindings.map((f) => (
          <div key={f.id} className="border border-border rounded p-3 text-xs">
            <p className={`${severityClass(f.severity)}`}>{f.message}</p>
            {f.actionHref && (
              <a
                href={f.actionHref}
                className="inline-block mt-2 text-primary underline"
                data-testid={`audit-scenario-link-${f.id}`}
              >
                Open financial scenarios
              </a>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * RECENT-ACTIVITY-RELOCATION — last-5 audit/upload/report timeline, moved here
 * from the Overview tab. Same event derivation as the former
 * `OverviewRecentActivity`: report generations + document uploads + the SED
 * rebuild, sorted newest-first.
 */
function AuditRecentActivity({
  site,
  siteReports,
  siteDocuments,
  onViewReport,
  onJumpToDocuments,
  onJumpToEvidence,
}: {
  site: SiteEntity;
  siteReports: SiteRagReport[];
  siteDocuments: Array<{ id?: string; fileName: string; uploadedAt: string }>;
  onViewReport?: (id: string) => void;
  onJumpToDocuments?: () => void;
  onJumpToEvidence?: () => void;
}) {
  const events = useMemo(() => {
    const out: Array<{ label: string; at: string; onClick?: () => void }> = [];
    for (const r of siteReports) {
      out.push({
        label: `${reportTypeLabel(r.reportType)} v${r.version}.${r.minorVersion ?? 0} generated`,
        at: r.createdAt,
        onClick: onViewReport ? () => onViewReport(r.id) : undefined,
      });
    }
    for (const d of siteDocuments) {
      out.push({
        label: `${resolveSiteDocumentFileName(d as { id?: string; fileName?: string; name?: unknown }, d.id)} uploaded`,
        at: d.uploadedAt,
        onClick: onJumpToDocuments,
      });
    }
    if (site.sedComputedAt) {
      out.push({
        label: 'Site evidence rebuilt',
        at: site.sedComputedAt,
        onClick: onJumpToEvidence,
      });
    }
    // Collapse exact-duplicate events (same label + timestamp) — e.g. a
    // document present twice in `siteDocuments` would otherwise render two
    // identical "X uploaded" rows (live-QA: a duplicated upload record showed
    // the same file twice). Distinct timestamps stay distinct.
    const seen = new Set<string>();
    return out
      .filter((e) => e.at)
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
      .filter((e) => {
        const k = `${e.label}${e.at}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .slice(0, 5);
  }, [site.sedComputedAt, siteReports, siteDocuments, onViewReport, onJumpToDocuments, onJumpToEvidence]);

  if (events.length === 0) return null;

  return (
    <section className="da-panel" data-testid="audit-recent-activity">
      <div className="da-panel__head">
        <span className="da-type-eyebrow">
          <Activity className="inline-block w-3 h-3 mr-1 align-[-1px]" aria-hidden /> Recent activity
        </span>
        <Chip>last {events.length}</Chip>
      </div>
      <div className="da-panel__body">
        <ul className="feed" data-testid="audit-recent-activity-list">
          {events.map((e, i) => {
            const ts = new Date(e.at).getTime();
            const rel = Number.isFinite(ts) ? relativeTime(ts) : '';
            return (
              <li key={`${e.at}-${i}`}>
                <button
                  type="button"
                  onClick={e.onClick}
                  disabled={!e.onClick}
                  className="feed__row"
                  data-testid={`audit-recent-activity-row-${i}`}
                >
                  <span className="feed__l">
                    <span className="feed__dot" aria-hidden />
                    <span className="feed__txt">{e.label}</span>
                  </span>
                  <span className="feed__t">{rel}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

/**
 * RECENT-ACTIVITY-RELOCATION — the Recent activity timeline (formerly Overview
 * ZONE 3) now lives at the bottom of the Audit tab, paired with a compact
 * report-card list. The host (SiteLandingPage) threads the resolved
 * site reports/documents + the same navigation callbacks the Overview used.
 */
export interface SiteAuditTabProps {
  site: SiteEntity;
  /** Reports for this site (host-resolved, freshest-first). Used by the
   *  relocated Recent activity + the report-card section. */
  siteReports?: SiteRagReport[];
  /** Documents for this site, for the upload events in Recent activity. */
  siteDocuments?: Array<{ id?: string; fileName: string; uploadedAt: string }>;
  onViewReport?: (reportId: string) => void;
  onJumpToDocuments?: () => void;
  onJumpToEvidence?: () => void;
}

function AuditDeepValidation({
  site,
  siteReports,
  findings,
  result,
  reportsWithProviderRelevance,
  stage,
}: {
  site: SiteEntity;
  siteReports: SiteRagReport[];
  findings: UserAuditFinding[];
  result: UserAuditResult;
  reportsWithProviderRelevance: SiteRagReport[];
  stage: 1 | 2;
}) {
  // Live per-family counts on the collapsed toggle line, so users know
  // whether opening the disclosure is worth it ("caveats 2 · citations 0").
  const summary = useMemo(() => {
    const caveats = siteReports.reduce((n, r) => n + (r.qaFindings?.caveatHonesty?.findings?.length ?? 0), 0);
    const citations = siteReports.reduce((n, r) => n + (r.qaFindings?.citationIntegrityViolations?.length ?? 0), 0);
    const provenance = siteReports.reduce((n, r) => n + (r.qaFindings?.docProvenanceFindings?.length ?? 0), 0);
    const coherence = Object.values(result.sectionCoherenceByReport ?? {}).reduce(
      (n, v) => n + (v?.findings.length ?? 0), 0,
    );
    // Parcel variance is detector-computed inside its panel — no cheap count.
    return `provenance ${provenance} · caveats ${caveats} · citations ${citations} · coherence ${coherence} · parcel`;
  }, [siteReports, result.sectionCoherenceByReport]);
  return (
    <DeepValidationSurfaces summary={summary}>
      <ScenarioSensitivitySection findings={findings} />
      <DistributionSanitySection siteId={site.id} reports={siteReports} />
      <DocProvenanceSection site={site} reports={siteReports} />
      {stage === 2 && <QuoteCitationIntegritySection site={site} />}
      <ParcelVariancePanel site={site} />
      <ParcelTopologyPanel site={site} />
      <CaveatHonestyPanel reports={siteReports} />
      <QualitativeClaimAuditPanel reports={siteReports} auditResult={result} />
      <SectionCoherencePanel reports={siteReports} auditResult={result} />
      <CitationSupportPanel siteId={site.id} reports={siteReports} auditResult={result} />
      {reportsWithProviderRelevance.length > 0 && (
        <ProviderRelevancePanel reports={reportsWithProviderRelevance} />
      )}
    </DeepValidationSurfaces>
  );
}

export function SiteAuditTab({
  site,
  siteReports: siteReportsProp,
  siteDocuments = [],
  onViewReport,
  onJumpToDocuments,
  onJumpToEvidence,
}: SiteAuditTabProps) {
  const auditEntry = useStore((s) => s.auditBySite?.[site.id]);
  const runAuditForSite = useStore((s) => s.runAuditForSite);
  const recordAuditOverride = useStore((s) => s.recordAuditOverride);
  const removeAuditOverride = useStore((s) => s.removeAuditOverride);
  const generateTypedReport = useStore((s) => s.generateTypedReport);
  const allReports = useStore((s) => s.siteRag.reports);
  const derivedSiteReports = useMemo(
    () => allReports.filter((r) => r.siteId === site.id),
    [allReports, site.id],
  );
  // Prefer the host-resolved report set (matches the Overview/Reports tabs'
  // broader site-match heuristic); fall back to the strict store filter.
  const siteReports = siteReportsProp ?? derivedSiteReports;
  const scopedSiteDocuments = useMemo(() => {
    const seen = new Set<string>();
    const docs: Array<{ id: string; name: string; fileName: string; siteId?: string; category?: string }> = [];
    const pushDoc = (
      d:
        | { id?: string; name?: string; fileName?: string; siteId?: string; category?: string }
        | undefined
        | null,
    ) => {
      if (!d || typeof d.id !== 'string' || !d.id || seen.has(d.id)) return;
      if (d.siteId && d.siteId !== site.id) return;
      const resolvedName =
        typeof d.name === 'string' && d.name.trim()
          ? d.name
          : typeof d.fileName === 'string' && d.fileName.trim()
            ? d.fileName
            : d.id;
      seen.add(d.id);
      docs.push({
        id: d.id,
        name: resolvedName,
        fileName: d.fileName ?? resolvedName,
        ...(d.siteId ? { siteId: d.siteId } : {}),
        ...(d.category ? { category: d.category } : {}),
      });
    };
    for (const d of siteDocuments) pushDoc(d as { id?: string; name?: string; fileName?: string; siteId?: string; category?: string });
    for (const d of site.documents ?? []) pushDoc(d as { id?: string; name?: string; fileName?: string; siteId?: string; category?: string });
    for (const r of siteReports) {
      if (r.siteId && r.siteId !== site.id) continue;
      for (const d of r.documents ?? []) pushDoc(d as { id?: string; name?: string; fileName?: string; siteId?: string; category?: string });
    }
    return docs;
  }, [siteDocuments, site.documents, site.id, siteReports]);
  const composite = useStore(s => s.portfolio?.composites?.[site.id]);
  const stage = resolveSiteDecisionStage(composite).currentStage;
  const stageReports = useMemo(() => stage === 2 ? siteReports
    : siteReports.filter(report => REPORT_TYPE_REGISTRY[(report.reportType ?? 'feasibility') as ReportTypeId]?.stage !== 2), [siteReports, stage]);
  const result = useMemo(() => auditEntry?.lastResult
    ? auditResultForStage(auditEntry.lastResult, siteReports, stage) : undefined, [auditEntry?.lastResult, siteReports, stage]);
  const deferredFindings = (auditEntry?.lastResult?.findings.length ?? 0) - (result?.findings.length ?? 0);
  const running = auditEntry?.running ?? false;
  const lastRunAt = auditEntry?.lastRunAt ?? result?.lastRunAt;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [debounceHint, setDebounceHint] = useState<string | null>(null);
  const [reconcilingId, setReconcilingId] = useState<string | null>(null);
  const [reconcileNotice, setReconcileNotice] = useState<string | null>(null);
  // RP2 — the report a recorded reconcile decision wants regenerated, so the
  // notice can offer an IN-PLACE "Regenerate now" instead of a hard navigation.
  const [reconcileRegenReportId, setReconcileRegenReportId] = useState<string | null>(null);
  const [reconcileRegenerating, setReconcileRegenerating] = useState(false);

  const counts = result?.severityCounts ?? { block: 0, warn: 0, info: 0 };
  const findings = result?.findings ?? [];

  // One-shot `?report=<id>` read (same deep-link contract as
  // ReportQualityAuditSection) — preselects the ledger's report filter so the
  // blocked-publish dialog / reader banner land on that report's fix queue.
  const requestedReportId = useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('report'); } catch { return null; }
  }, []);

  const sortedFindings = useMemo(
    () => [...findings].sort((a, b) => {
      const order = { block: 0, warn: 1, info: 2 };
      return order[a.severity] - order[b.severity];
    }),
    [findings],
  );


  const reportsWithProviderRelevance = useMemo(
    () => stageReports.filter((r) => (r.qaFindings?.providerRelevance?.length ?? 0) > 0),
    [stageReports],
  );

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const startReconcile = useCallback((id: string) => {
    setReconcileNotice(null);
    setReconcilingId(id);
  }, []);

  const cancelReconcile = useCallback(() => setReconcilingId(null), []);

  /**
   * P3 — findings the user has already AFFIRMED/VALIDATED, derived from the
   * persisted `auditOverrides`. A validation override is recorded under the
   * deterministic id `validate:{findingId}` so the affirmation survives reloads
   * and reuses the SAME `auditOverrides` plumbing the reconcile decision uses.
   */
  const activeSiteAuditOverrides = useMemo(
    () => resolveActiveAuditOverrides(site.auditOverrides),
    [site.auditOverrides],
  );
  const validatedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const o of activeSiteAuditOverrides) {
      const baseId = stripOverrideVersionSuffix(o.logicalKey ?? o.id);
      if (('type' in o && o.type === 'validate') || baseId.startsWith('validate:')) {
        ids.add(baseId.replace('validate:', ''));
      }
    }
    return ids;
  }, [activeSiteAuditOverrides]);

  /**
   * Split the worklist from settled decisions. A finding is settled when the
   * audit engine annotated it (`validated` — survives re-runs via stable key)
   * OR a just-clicked override matches (`validatedIds` — instant feedback
   * before the forced re-run lands). Settled findings render in a collapsed
   * group with Undo instead of vanishing silently.
   */
  // Phase-3 — dismissed (parked) findings, instant-feedback set like
  // `validatedIds`. Snoozes with an expired `until` do NOT park.
  const dismissedIds = useMemo(() => {
    const ids = new Set<string>();
    const now = Date.now();
    for (const o of activeSiteAuditOverrides) {
      if ('type' in o && o.type === 'dismiss' && (!o.until || new Date(o.until).getTime() > now)) {
        ids.add(o.findingId);
      }
    }
    return ids;
  }, [activeSiteAuditOverrides]);

  const { activeFindings, validatedFindings, dismissedFindings } = useMemo(() => {
    const active: UserAuditFinding[] = [];
    const settled: UserAuditFinding[] = [];
    const parked: UserAuditFinding[] = [];
    for (const f of sortedFindings) {
      if (f.validated || validatedIds.has(f.id)) settled.push(f);
      else if (f.dismissed || dismissedIds.has(f.id)) parked.push(f);
      else active.push(f);
    }
    return { activeFindings: active, validatedFindings: settled, dismissedFindings: parked };
  }, [sortedFindings, validatedIds, dismissedIds]);

  const [showValidated, setShowValidated] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);

  /** Undo a validation: remove the override, then recompute immediately. */
  const handleUndoValidate = useCallback(
    async (finding: UserAuditFinding) => {
      const overrideId = finding.validatedByOverrideId ?? `validate:${finding.id}`;
      try {
        await removeAuditOverride(site.id, overrideId);
        void runAuditForSite(site.id, { force: true });
      } catch {
        setReconcileNotice('Could not undo the validation. Please retry.');
      }
    },
    [site.id, removeAuditOverride, runAuditForSite],
  );

  /** Phase-3 — park a finding (no correctness assertion; undoable). */
  const handleDismiss = useCallback(
    async (finding: UserAuditFinding) => {
      try {
        await recordAuditOverride(site.id, {
          id: `dismiss:${finding.id}`,
          type: 'dismiss',
          findingId: finding.id,
          stableKey: makeStableFindingKey(finding.checkId, finding.reportType, finding.message),
          rationale: `Dismissed from the audit worklist (${finding.checkId}).`,
          ...(finding.reportType ? { reportTypeId: finding.reportType } : {}),
        });
        setReconcileNotice('Finding dismissed — it no longer counts against trust. Undo any time.');
        void runAuditForSite(site.id, { force: true });
      } catch {
        setReconcileNotice('Could not dismiss the finding. Please retry.');
      }
    },
    [site.id, recordAuditOverride, runAuditForSite],
  );

  /** Phase-3 — full append-only decision trail (active, superseded, retracted, retain-unknown, retract), newest first. */
  const decisionOverrides = useMemo(
    () =>
      [...(site.auditOverrides ?? [])]
        .filter(
          (o) =>
            'type' in o &&
            (o.type === 'validate' ||
              o.type === 'reconcile' ||
              o.type === 'dismiss' ||
              o.type === 'retain-unknown' ||
              o.type === 'retract'),
        )
        .sort((a, b) => {
          const seqDiff = (b.sequence ?? 0) - (a.sequence ?? 0);
          if (seqDiff !== 0) return seqDiff;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }),
    [site.auditOverrides],
  );
  const activeDecisionIdSet = useMemo(
    () => new Set(activeSiteAuditOverrides.map((o) => o.id)),
    [activeSiteAuditOverrides],
  );
  const retractedDecisionIdSet = useMemo(
    () =>
      new Set(
        (site.auditOverrides ?? [])
          .filter((o): o is Extract<typeof o, { type: 'retract' }> => 'type' in o && o.type === 'retract')
          .map((o) => o.retractsOverrideId),
      ),
    [site.auditOverrides],
  );

  const handleUndoDecision = useCallback(
    async (overrideId: string) => {
      try {
        await removeAuditOverride(site.id, overrideId);
        void runAuditForSite(site.id, { force: true });
      } catch {
        setReconcileNotice('Could not undo the decision. Please retry.');
      }
    },
    [site.id, removeAuditOverride, runAuditForSite],
  );

  const handleUndoDismiss = useCallback(
    async (finding: UserAuditFinding) => {
      const overrideId = finding.dismissedByOverrideId ?? `dismiss:${finding.id}`;
      try {
        await removeAuditOverride(site.id, overrideId);
        void runAuditForSite(site.id, { force: true });
      } catch {
        setReconcileNotice('Could not restore the finding. Please retry.');
      }
    },
    [site.id, removeAuditOverride, runAuditForSite],
  );

  /**
   * P3 — VALIDATE: the user affirms a finding's value is correct (accept /
   * confirm), which clears it from the active worklist. We persist an
   * affirmation as a site-level `auditOverride` (the same breadcrumb mechanism
   * as the reconcile decision — never silent) keyed `validate:{findingId}`.
   *
   * NOTE: `SiteAuditOverride.gate` is a closed union with no dedicated
   * affirmation slot, so we reuse `provider-precedence` (the gate the reconcile
   * decision already records under) and distinguish the entry by its id prefix
   * + rationale. This invents no SED path (no SEDAmendment) — it only records
   * that the value was reviewed and accepted as-is.
   */
  const handleValidate = useCallback(
    async (finding: UserAuditFinding) => {
      const decision = {
        id: `validate:${finding.id}`,
        type: 'validate' as const,
        checkId: finding.checkId,
        factKey: finding.message.split(':')[0]?.trim() ?? finding.checkId,
        // Survives regeneration: the exact-id match dies with the report id.
        stableKey: makeStableFindingKey(finding.checkId, finding.reportType, finding.message),
        rationale: `Validated: value reviewed and confirmed correct as-is (${finding.checkId}).`,
        ...(finding.reportType ? { reportTypeId: finding.reportType } : {}),
      };
      try {
        // Store action (not raw updateSite): stamps createdBy + emits the
        // audit_log event; the deterministic id upserts on re-decide.
        await recordAuditOverride(site.id, decision);
        setReconcileNotice(`Value confirmed — this finding is marked as validated.`);
        setReconcileRegenReportId(null);
        // Recompute immediately so the counts, trust gauge and "N to review"
        // pill reflect the validation — without this the row keeps counting
        // until a manual re-run clears the 30s debounce.
        void runAuditForSite(site.id, { force: true });
      } catch {
        setReconcileNotice('Could not record the validation. Please retry.');
      }
    },
    [site.id, recordAuditOverride, runAuditForSite],
  );

  /**
   * U6 / RP2 — apply a user-picked canonical value for an internal-number
   * inconsistency. We record the chosen value as a site-level reconciliation
   * decision (the audit breadcrumb — kept), then offer an IN-PLACE "Regenerate
   * now" so the internal-number reconciler (`applyInternalNumberGate` /
   * `resolveCanonicalValue`) re-applies the canonical value through the SAME
   * generateTypedReport 'update' pipeline the rest of the platform uses.
   *
   * RP2 — the prior implementation did a hard `window.location.assign(actionHref)`
   * (a full page navigation that dropped the user out of the audit console and
   * nothing in the resolve loop consumed the decision). It is replaced with an
   * in-place regenerate: the decision is the resolution mechanism the pipeline
   * reads, and regeneration happens without leaving the tab.
   *
   * NOTE: an internal-number inconsistency is a REPORT-level conflict (the same
   * number cited with different values in one report), not a single SED slot —
   * `UserAuditFinding` carries no dotted `variablePath`, so we deliberately do
   * NOT fabricate a SEDAmendment here (that would invent a path the SED schema
   * guard would reject). The canonical value is applied at regeneration time.
   */
  const applyReconcile = useCallback(
    async (finding: UserAuditFinding, canonicalValue: string) => {
      setReconcilingId(null);
      // An override without a canonical fact key can never be matched back to
      // a finding at regeneration time — refuse to record a dead decision.
      if (!finding.canonicalFactKey) {
        setReconcileNotice(
          'This finding has no canonical fact key — use Change to correct the value in place.',
        );
        setReconcileRegenReportId(null);
        return;
      }
      const decision = {
        id: `recon:${finding.id}`,
        type: 'reconcile' as const,
        canonicalKey: finding.canonicalFactKey,
        canonicalValue: canonicalValue,
        rationale: `Internal-number reconcile: canonical value set to ${formatCanonicalValue(canonicalValue)}. Re-run report to apply.`,
        ...(finding.reportType ? { reportTypeId: finding.reportType } : {}),
      };
      try {
        // Store action (not raw updateSite): stamps createdBy + emits the
        // audit_log event; the deterministic id upserts on re-decide.
        await recordAuditOverride(site.id, decision);
      } catch {
        setReconcileNotice('Could not record the reconcile decision. Please retry.');
        setReconcileRegenReportId(null);
        return;
      }
      // Resolve the affected report (the finding's own report id, else the
      // freshest matching-type report) so the notice can regenerate it in place.
      const targetReport =
        (finding.reportId ? siteReports.find((r) => r.id === finding.reportId) : undefined) ??
        (finding.reportType
          ? [...siteReports]
              .filter((r) => (r.reportType ?? 'feasibility') === finding.reportType)
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
          : undefined);
      setReconcileRegenReportId(targetReport?.id ?? null);
      setReconcileNotice(
        targetReport
          ? `Canonical value ${formatCanonicalValue(canonicalValue)} recorded. Regenerate the report to apply it.`
          : `Canonical value ${formatCanonicalValue(canonicalValue)} recorded. Re-run the report to apply it.`,
      );
      // The in-report conflict clears only on regeneration, but the recorded
      // canonical settles the R-79 cross-report divergence right away —
      // recompute so those counts update without waiting out the debounce.
      void runAuditForSite(site.id, { force: true });
    },
    [site.id, recordAuditOverride, siteReports, runAuditForSite],
  );

  /**
   * RP2 (generalized) — regenerate ANY report IN PLACE (reuses the
   * generateTypedReport 'update' fast-path + getRerunDefaults to reproduce the
   * parent's framing). Used by the reconcile notice's "Regenerate now" CTA and
   * by every finding whose primary fix is `regenerate` (tier-* content checks,
   * R-78 precedence re-grounding).
   */
  const regenerateReport = useCallback(
    async (reportId: string, notice?: string): Promise<boolean> => {
      const parent = siteReports.find((r) => r.id === reportId);
      if (!parent) {
        setReconcileNotice('Report no longer available — re-run it from the Reports tab.');
        return false;
      }
      try {
        const defaults = getRerunDefaults(parent);
        const documentSelection =
          defaults.documentRefs && defaults.documentRefs.length > 0
            ? { documentRefs: defaults.documentRefs, documentSelectionMode: 'mixed' as const }
            : undefined;
        await generateTypedReport(
          site.id,
          (parent.reportType ?? 'feasibility') as ReportTypeId,
          defaults.typedPreferences ?? {},
          'update',
          parent.id,
          defaults.archetypeOverride,
          documentSelection,
        );
        setReconcileNotice(notice ?? 'Regeneration started.');
        return true;
      } catch (e) {
        setReconcileNotice(`Regeneration failed: ${(e as Error).message}`);
        return false;
      }
    },
    [siteReports, generateTypedReport, site.id],
  );

  const handleReconcileRegenerate = useCallback(async () => {
    if (!reconcileRegenReportId) return;
    setReconcileRegenerating(true);
    try {
      const ok = await regenerateReport(
        reconcileRegenReportId,
        'Regeneration started — the canonical value will be applied.',
      );
      if (ok) setReconcileRegenReportId(null);
    } finally {
      setReconcileRegenerating(false);
    }
  }, [reconcileRegenReportId, regenerateReport]);

  /** Decision history is undo infrastructure, not reading material —
   *  collapsed until the user needs to reverse something. */
  const [showDecisions, setShowDecisions] = useState(false);

  /** Report-level regenerate — ONE run per report applies every pending fix
   *  (recorded reconciles, verified values, depth expansion). The spinner id
   *  is the REPORT id so every row of the group reflects the in-flight run. */
  const [regeneratingFindingId, setRegeneratingFindingId] = useState<string | null>(null);
  const handleFindingRegenerate = useCallback(
    async (reportId: string, _findingId: string) => {
      setRegeneratingFindingId(reportId);
      try {
        await regenerateReport(reportId, 'Regeneration started — the audit re-runs when it completes.');
      } finally {
        setRegeneratingFindingId(null);
      }
    },
    [regenerateReport],
  );

  /**
   * `apply-directive` fix — focus the Report-quality section on the finding's
   * report so the user lands in the sanity-finding apply/ignore flow.
   */
  const [qualityFocus, setQualityFocus] = useState<{ reportId: string; nonce: number } | null>(null);
  const handleFocusQuality = useCallback((reportId: string) => {
    // The nonce re-triggers the section's focus effect on repeat clicks.
    setQualityFocus((prev) => ({ reportId, nonce: (prev?.nonce ?? 0) + 1 }));
  }, []);

  /**
   * R-78 secondary fix — DISPUTE the tier-1 value via the canonical SED
   * amendment flow (ResolveAmendmentDialog → saveSEDAmendment, the ONE
   * persist path). The SED slot currently holds the tier-1 value; the report's
   * value is pre-filled as the alternative the user is asserting.
   */
  const saveSEDAmendment = useStore((s) => s.saveSEDAmendment);
  const [amendDraft, setAmendDraft] = useState<ResolveAmendmentDraft | null>(null);
  const handleAmendSed = useCallback(
    (finding: UserAuditFinding, fix: Extract<FindingFix, { kind: 'amend-sed' }>) => {
      if (!site.sedId || site.sedVersion == null) {
        setReconcileNotice('This site has no evidence document (SED) to amend.');
        return;
      }
      setAmendDraft({
        sedId: site.sedId,
        sedVersion: site.sedVersion,
        variablePath: fix.variablePath,
        variableLabel: fix.variableLabel,
        domain: fix.domain,
        oldValue: fix.oldValue,
        suggestedValue: fix.suggestedValue,
        defaultReason: `Disputing verified provider value for ${fix.variableLabel} (${finding.checkId}).`,
        issueType: 'incorrect',
      });
    },
    [site.sedId, site.sedVersion],
  );

  /** B2/R-82 — file the finding as a platform bug via the escalation API. */
  const handleEscalate = useCallback(
    async (finding: UserAuditFinding) => {
      try {
        const { escalateAuditFinding, toEscalationFinding } = await import('../../services/auditClient');
        const bugReportId = await escalateAuditFinding(site.id, toEscalationFinding(finding));
        setReconcileNotice(`Reported to Datacrate (bug ${bugReportId}). We'll take it from here.`);
      } catch (e) {
        setReconcileNotice(`Could not send the report: ${(e as Error).message}`);
      }
    },
    [site.id],
  );

  /**
   * R-65 one-click sync — adopt the SED's typed value into Project Intent via
   * `updateSiteProjectIntent` (intent is NEVER amended through the SED path).
   * Reuses `buildIntentSyncPatches`, scoped to the finding's divergence.
   */
  const updateSiteProjectIntent = useStore((s) => s.updateSiteProjectIntent);
  const findDivergenceFor = useCallback(
    (finding: UserAuditFinding) =>
      result?.intentSedReconciliation?.divergences.find(
        (d) => finding.message.startsWith(d.field) && d.sedSyncValue !== undefined,
      ),
    [result?.intentSedReconciliation],
  );
  const canSyncIntentFor = useCallback(
    (finding: UserAuditFinding) =>
      finding.checkId === 'R-65' && Boolean(site.projectIntent) && Boolean(findDivergenceFor(finding)),
    [findDivergenceFor, site.projectIntent],
  );
  const handleSyncIntent = useCallback(
    async (finding: UserAuditFinding) => {
      const divergence = findDivergenceFor(finding);
      const intent = site.projectIntent;
      if (!divergence || !intent) return;
      const patch = buildIntentSyncPatches(intent, { divergences: [divergence], aligned: false });
      if (Object.keys(patch).length === 0) {
        setReconcileNotice('This divergence has no typed SED value to adopt — edit the intent directly.');
        return;
      }
      try {
        await updateSiteProjectIntent(site.id, { ...intent, ...patch });
        setReconcileNotice(`Project Intent updated from SED (${divergence.field}).`);
        void runAuditForSite(site.id, { force: true });
      } catch (e) {
        setReconcileNotice(`Intent update failed: ${(e as Error).message}`);
      }
    },
    [findDivergenceFor, site.projectIntent, site.id, updateSiteProjectIntent, runAuditForSite],
  );

  const handleRerun = useCallback(async () => {
    setDebounceHint(null);
    const out = await runAuditForSite(site.id);
    if (!out && auditEntry?.lastDebouncedAt) {
      const elapsed = Date.now() - new Date(auditEntry.lastDebouncedAt).getTime();
      if (elapsed < AUDIT_DEBOUNCE_MS) {
        setDebounceHint(`Please wait ${Math.ceil((AUDIT_DEBOUNCE_MS - elapsed) / 1000)}s before re-running.`);
      }
    }
  }, [auditEntry?.lastDebouncedAt, runAuditForSite, site.id]);

  const activeGenerations = useStore((s) => s.siteRag.activeGenerations);
  const isGenerating = useMemo(() => {
    if (!activeGenerations) return false;
    return Object.values(activeGenerations).some((run) => run.siteId === site.id);
  }, [activeGenerations, site.id]);

  const prevGenerating = useRef(isGenerating);
  useEffect(() => {
    if (prevGenerating.current && !isGenerating) {
      handleRerun();
    }
    prevGenerating.current = isGenerating;
  }, [isGenerating, handleRerun]);

  const autoRefreshedRunAtRef = useRef<string | null>(null);
  useEffect(() => {
    if (running || isGenerating || !auditEntry?.lastResult) return;
    const lastRunAtIso = auditEntry.lastRunAt ?? auditEntry.lastResult.lastRunAt;
    const lastRunMs = lastRunAtIso ? new Date(lastRunAtIso).getTime() : NaN;
    const recordedIds = new Set(auditEntry.lastResult.reportIds ?? []);
    const hasUnrecordedLatestReport = Boolean(
      site.latestReportId
      && !recordedIds.has(site.latestReportId)
      && siteReports.some((r) => r.id === site.latestReportId),
    );
    const sedComputedMs = site.sedComputedAt ? new Date(site.sedComputedAt).getTime() : NaN;
    const sedNewerThanAudit = Number.isFinite(sedComputedMs) && Number.isFinite(lastRunMs) && sedComputedMs > lastRunMs;
    if (!hasUnrecordedLatestReport && !sedNewerThanAudit) return;
    const guardKey = `${site.id}:${lastRunAtIso ?? "none"}:${site.latestReportId ?? "none"}:${site.sedComputedAt ?? "none"}`;
    if (autoRefreshedRunAtRef.current === guardKey) return;
    autoRefreshedRunAtRef.current = guardKey;
    void runAuditForSite(site.id, { force: true });
  }, [
    auditEntry?.lastResult,
    auditEntry?.lastRunAt,
    isGenerating,
    runAuditForSite,
    running,
    site.id,
    site.latestReportId,
    site.sedComputedAt,
    siteReports,
  ]);

  const isClean = result && findings.length === 0;

  // DATA-TRUST CONSOLE — derive the four plain-language trust dimensions + the
  // overall 0–100 trust score from the (still fully-run) audit findings.
  const trust = useMemo(() => deriveTrust(findings), [findings]);
  const trustTone: 'clean' | 'warn' | 'block' =
    counts.block > 0 ? 'block' : counts.warn > 0 ? 'warn' : 'clean';
  // Map each tour-anchored dimension onto its ledger row so the audit
  // onboarding tour (AuditTourSteps) keeps resolving its targets.
  const TOUR_ANCHOR: Partial<Record<TrustDimensionId, string>> = {
    citation: 'citation-integrity-row',
    conflicts: 'provider-precedence-row',
    intent: 'reconciliation-row',
  };
  // The lede must name the dimension that ACTUALLY holds the score back.
  // It used to hardcode "a blocking data conflict" for any block — live on
  // Triple Threat that read while Data conflicts showed 0 CLEAR and the
  // block sat in Provenance. trustHeadline derives it from the breakdown.
  const lede = isClean
    ? 'No findings in the current stage view. Report holds and final decision requirements remain governed separately.'
    : result
      ? stage === 1
        ? trustHeadline(trust)
          .replace("before this site's reports are dispatch-ready.", 'before using the screening findings.')
          .replace('this site’s numbers are dispatch-ready.', 'screening evidence has no outstanding trust findings.')
        : trustHeadline(trust)
      : 'Run an audit to score this site’s data trust across conflicts, provenance, citation integrity and intent alignment.';

  /** Plain-language arithmetic for the score tooltip — every point traceable. */
  const trustExplain = useMemo(() => {
    const b = trust.breakdown;
    const lines = b.lines.map(
      (l) => `− ${Math.round(l.points)} · ${l.count} ${l.severity}${l.count === 1 ? '' : 's'} in ${l.dimensionName} (${l.each} each)`,
    );
    const parts = [
      `Starts at ${b.startingScore}.`,
      ...(lines.length ? lines : ['No findings charge any penalty.']),
      `= ${trust.score} / 100`,
    ];
    if (b.excluded.validated || b.excluded.dismissed) {
      const bits: string[] = [];
      if (b.excluded.validated) bits.push(`${b.excluded.validated} validated`);
      if (b.excluded.dismissed) bits.push(`${b.excluded.dismissed} dismissed`);
      parts.push(`Excluded (settled by you): ${bits.join(', ')}.`);
    }
    parts.push(`Weights — blocking ${b.weights.block}, warning ${b.weights.warn}, info ${b.weights.info}.`);
    return parts.join('\n');
  }, [trust]);

  const reviewCount = counts.block + counts.warn;
  const reviewVariant = counts.block > 0 ? 'nogo' : counts.warn > 0 ? 'cond' : 'go';

  return (
    <div className="da-tab" data-testid="site-audit-tab" data-tour="audit-tab-root">
      {/* Present the existing score and next action without changing audit gates. */}
      <header className="da-tabhead da-tabhead--hero" data-tour="severity-counts-card">
        <div className="da-tabhead__eyebrow">
          <span className="da-tabhead__dot" aria-hidden />
          <span className="da-type-eyebrow">
            <ShieldCheck className="inline-block w-3 h-3 mr-1 align-[-1px]" aria-hidden />
            AUDIT
            <span className="da-tabhead__ctx"> · DATA TRUST &amp; PROVENANCE</span>
          </span>
        </div>

        <div className="da-tabhead__top">
          <div className="da-tabhead__titlewrap">
            <h1 className="da-tabhead__title">Data Trust</h1>
            <p className="da-tabhead__desc da-type-body">
              Review conflicts, sources and citations. Open a finding to see what needs correcting.
            </p>
          </div>
          <div className="da-tabhead__actions">
            {isGenerating && (
              <span className="text-xs text-warning animate-pulse flex items-center gap-1.5 mr-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Report generation in-flight…
              </span>
            )}
            {result && (
              <StatusPill variant={reviewVariant}>
                {reviewCount > 0 ? `${reviewCount} to review` : 'All clear'}
              </StatusPill>
            )}
            {result && findings.length > 0 && (
              <Button type="button" variant="outline" onClick={() => {
                document.getElementById(`audit-worklist-${site.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}>Review findings</Button>
            )}
            <Button
              type="button"
              onClick={handleRerun}
              disabled={running || isGenerating}
              data-testid="audit-rerun-button"
              className="gap-2"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {running ? 'Auditing…' : 'Re-run Audit'}
            </Button>
          </div>
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div>
            <div
              className={`flex items-baseline gap-2 ${trustTone === 'block' ? 'text-danger' : trustTone === 'warn' ? 'text-warning' : 'text-text-primary'}`}
              role="img"
              aria-label={
                result
                  ? `Data trust score ${trust.score} out of 100. ${trustExplain.replace(/\n/g, ' ')}`
                  : 'Data trust score not yet computed'
              }
              // Hover shows the exact arithmetic — the score is checkable,
              // not a number the user has to take on faith.
              title={result ? trustExplain : undefined}
              data-testid="audit-trust-gauge"
              data-trust-explain={result ? trustExplain : undefined}
            >
              <span className="text-3xl font-semibold tabular-nums">{result ? trust.score : '—'}</span>
              <span className="text-sm text-text-muted">/ 100 data trust</span>
            </div>
            {result && trust.breakdown.lines.length > 0 && (
              <details className="mt-3 text-text-secondary" data-testid="audit-trust-explain">
                <summary className="da-type-caption" style={{ cursor: 'pointer' }}>
                  How is this scored?
                </summary>
                <ul className="da-type-caption" style={{ marginTop: '0.35rem', paddingLeft: '1rem' }}>
                  <li>Starts at {trust.breakdown.startingScore}.</li>
                  {trust.breakdown.lines.map((l) => (
                    <li key={`${l.dimensionId}-${l.severity}`}>
                      −{Math.round(l.points)} · {l.count} {l.severity}
                      {l.count === 1 ? '' : 's'} in {l.dimensionName}{' '}
                      <span className="text-text-secondary">({l.each} each)</span>
                    </li>
                  ))}
                  <li>
                    <strong>= {trust.score} / 100</strong>
                  </li>
                  {(trust.breakdown.excluded.validated > 0 || trust.breakdown.excluded.dismissed > 0) && (
                    <li className="text-text-secondary">
                      Excluded because you settled them:{' '}
                      {[
                        trust.breakdown.excluded.validated ? `${trust.breakdown.excluded.validated} validated` : null,
                        trust.breakdown.excluded.dismissed ? `${trust.breakdown.excluded.dismissed} dismissed` : null,
                      ].filter(Boolean).join(', ')}.
                    </li>
                  )}
                </ul>
              </details>
            )}
            <div className="mt-3 max-w-2xl">
              <p className="da-type-body">{lede}</p>
              <p className="da-type-caption" style={{ marginTop: '0.5rem' }}>
                Last audit · {lastRunAt ? relativeTime(new Date(lastRunAt).getTime()) : 'never'}
              </p>
            </div>
          </div>

          {/* Plain-language trust dimensions (NOT raw rule codes). */}
          <div data-testid="audit-signal-ledger">
            {trust.dimensions.map((dim) => {
              const anchor = TOUR_ANCHOR[dim.id];
              return anchor ? (
                <div key={dim.id} data-tour={anchor}>
                  <TrustSignalRow dim={dim} />
                </div>
              ) : (
                <TrustSignalRow key={dim.id} dim={dim} />
              );
            })}
          </div>
        </div>
      </header>

      {debounceHint && (
        <p className="text-sm text-warning" data-testid="audit-debounce-hint">{debounceHint}</p>
      )}


      <p className="text-sm text-text-secondary" data-testid="audit-stage-scope">
        {stage === 1 ? 'Stage 1 · Screening evidence and data correctness' : 'Stage 2 · Full validation'}
        {deferredFindings > 0 ? ` · ${deferredFindings} later-stage finding${deferredFindings === 1 ? '' : 's'} retained for validation` : ''}.
      </p>

      <ResolverSnapshotPanel site={site} />

      <AmendmentsAuditPanel site={site} reports={siteReports} />

      {/* Phase-3 — AUDIT DECISIONS: the durable validate/reconcile/dismiss
          trail with Undo. The ledger's settled badges already show CURRENT
          state, so this history is collapsed by default — it exists for the
          moment you need to reverse a decision, not as ten rows of raw
          rationale strings above the findings. Values are display-formatted
          and duplicate decision ids (a historical upsert glitch) dedupe. */}
      {decisionOverrides.length > 0 && (() => {
        const seenIds = new Set<string>();
        const deduped = decisionOverrides.filter((o) => {
          if (seenIds.has(o.id)) return false;
          seenIds.add(o.id);
          return true;
        });
        const prettyRationale = (r: string | undefined, id: string): string =>
          (r ?? id).replace(/-?\d+\.\d{4,}/g, (n) => formatCanonicalValue(n));
        return (
          <section data-testid="audit-decisions-history">
            <button
              type="button"
              className="da-band w-full text-left"
              aria-expanded={showDecisions}
              onClick={() => setShowDecisions((v) => !v)}
              data-testid="audit-decisions-toggle"
            >
              {showDecisions
                ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-text-muted" />
                : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-text-muted" />}
              <span className="da-band__label">Audit decisions</span>
              <span className="da-band__rule" />
              <span className="da-band__count">{deduped.length} on record · Undo lives here</span>
            </button>
            {showDecisions && (
              <ul className="rounded-lg border border-border divide-y divide-border-subtle">
                {deduped.map((o) => {
                  const isActiveDecision = o.type !== 'retract' && activeDecisionIdSet.has(o.id);
                  const decisionStatus =
                    o.type === 'retract'
                      ? 'retract'
                      : isActiveDecision
                        ? 'active'
                        : retractedDecisionIdSet.has(o.id)
                          ? 'retracted'
                          : 'superseded';
                  const lineageRef =
                    o.type === 'retract'
                      ? o.retractsOverrideId
                      : o.supersedesOverrideId;
                  return (
                    <li
                      key={o.id}
                      className={`flex flex-wrap items-center gap-2 px-3 py-2 ${isActiveDecision ? '' : 'opacity-70'}`}
                      data-testid={`audit-decision-${o.id}`}
                      data-status={decisionStatus}
                    >
                      <span className="rounded-sm border border-border px-1.5 py-0.5 type-section text-text-secondary">
                        {o.type}
                      </span>
                      <span
                        className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-muted"
                        data-testid={`audit-decision-status-${o.id}`}
                      >
                        {decisionStatus}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-xs text-text-primary" title={o.rationale}>
                        {prettyRationale(o.rationale, o.id)}
                        {lineageRef ? ` (ref: ${lineageRef})` : ''}
                      </span>
                      <span className="text-[11px] text-text-muted">
                        {o.createdBy?.email ?? '—'} · {relativeTime(new Date(o.createdAt).getTime())}
                      </span>
                      {isActiveDecision && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="shrink-0 text-xs"
                          onClick={() => void handleUndoDecision(o.id)}
                          data-testid={`audit-decision-undo-${o.id}`}
                        >
                          Undo
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })()}

      <UserAuthoredConflictsPanel site={site} />

      {!result && !running && (
        <EmptyState
          icon={ShieldCheck}
          title="No audit run yet"
          body="Run the audit to score this site’s data trust across conflicts, provenance, citation integrity and intent alignment."
          action={
            <Button type="button" variant="outline" onClick={handleRerun}>
              Run Audit
            </Button>
          }
          data-testid="audit-empty-state"
        />
      )}

      {isClean && (
        <EmptyState
          icon={CheckCircle2}
          title="No findings — site is clean in this stage view."
          body="No findings apply to the current stage. Stored report holds and final decision requirements remain unchanged."
          action={
            <Button type="button" variant="outline" onClick={handleRerun}>
              Re-run Audit
            </Button>
          }
          data-testid="audit-clean-state"
        />
      )}

      {result && findings.length > 0 && (
        <>
          <section id={`audit-worklist-${site.id}`} className="scroll-mt-4">
            <div className="da-band">
              <span className="da-band__label">Data conflicts &amp; findings</span>
              <span className="da-band__rule" />
              {/* P — same "to review" arithmetic as the Data Trust card
                  (block + warn); every severity is spelled out so nothing has
                  to be inferred by subtraction. */}
              <span className="da-band__count">
                {counts.block + counts.warn} to review · {counts.block} blocking
                {counts.warn > 0 ? ` · ${counts.warn} warn` : ''}
                {counts.info > 0 ? ` · ${counts.info} info` : ''}
              </span>
            </div>
            {reconcileNotice && (
              <div
                className="mb-2 flex flex-wrap items-center gap-2"
                role="status"
                data-testid="audit-reconcile-notice"
              >
                <p className="text-xs text-success">{reconcileNotice}</p>
                {/* RP2 — in-place regenerate (replaces the old hard navigation). */}
                {reconcileRegenReportId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleReconcileRegenerate()}
                    disabled={reconcileRegenerating}
                    data-testid="audit-reconcile-regenerate"
                  >
                    {reconcileRegenerating ? 'Regenerating…' : 'Regenerate now'}
                  </Button>
                )}
              </div>
            )}
            {activeFindings.length > 0 ? (
              <AuditFindingsLedger
                stage={stage}
                findings={activeFindings}
                expandedIds={expandedIds}
                validatedIds={validatedIds}
                siteId={site.id}
                sedId={site.sedId}
                siteReports={siteReports}
                canSyncIntentFor={canSyncIntentFor}
                onToggleExpand={toggleExpand}
                reconcilingId={reconcilingId}
                onStartReconcile={startReconcile}
                onCancelReconcile={cancelReconcile}
                onApplyReconcile={applyReconcile}
                onValidate={handleValidate}
                onRegenerate={(reportId, findingId) => void handleFindingRegenerate(reportId, findingId)}
                onFocusQuality={handleFocusQuality}
                onAmendSed={handleAmendSed}
                onSyncIntent={(finding) => void handleSyncIntent(finding)}
                onEscalate={(finding) => void handleEscalate(finding)}
                onDismiss={(finding) => void handleDismiss(finding)}
                regeneratingId={regeneratingFindingId}
                initialReportId={requestedReportId}
                onViewReport={onViewReport}
              />
            ) : (
              <p className="text-xs text-text-muted px-1 py-2" data-testid="audit-all-settled">
                All findings are settled — every one was reviewed and confirmed.
              </p>
            )}
            {validatedFindings.length > 0 && (
              <div className="mt-2 border border-border rounded-lg" data-testid="audit-validated-group">
                <button
                  type="button"
                  className="flex w-full items-center gap-1.5 px-2.5 py-2 text-xs text-text-secondary hover:text-text-primary"
                  onClick={() => setShowValidated((v) => !v)}
                  aria-expanded={showValidated}
                  data-testid="audit-validated-toggle"
                >
                  {showValidated ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  <CheckCheck className="h-3.5 w-3.5 text-success" />
                  Validated ({validatedFindings.length}) — reviewed and confirmed correct as-is
                </button>
                {/* hidden-not-unmounted so testids stay resolvable when collapsed */}
                <ul className={showValidated ? 'px-2.5 pb-2 space-y-1' : 'hidden'}>
                  {validatedFindings.map((f) => (
                    <li
                      key={f.id}
                      className="flex items-start justify-between gap-2 rounded-md bg-muted/40 px-2 py-1.5"
                      data-testid={`audit-validated-row-${f.id}`}
                    >
                      <span className="min-w-0 text-xs text-text-muted line-through decoration-border">
                        {f.message}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="shrink-0 text-xs"
                        onClick={() => void handleUndoValidate(f)}
                        data-testid={`audit-validated-undo-${f.id}`}
                      >
                        Undo
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {dismissedFindings.length > 0 && (
              <div className="mt-2 border border-border rounded-lg" data-testid="audit-dismissed-group">
                <button
                  type="button"
                  className="flex w-full items-center gap-1.5 px-2.5 py-2 text-xs text-text-secondary hover:text-text-primary"
                  onClick={() => setShowDismissed((v) => !v)}
                  aria-expanded={showDismissed}
                  data-testid="audit-dismissed-toggle"
                >
                  {showDismissed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  Dismissed ({dismissedFindings.length}) — parked without a correctness decision
                </button>
                {/* hidden-not-unmounted so testids stay resolvable when collapsed */}
                <ul className={showDismissed ? 'px-2.5 pb-2 space-y-1' : 'hidden'}>
                  {dismissedFindings.map((f) => (
                    <li
                      key={f.id}
                      className="flex items-start justify-between gap-2 rounded-md bg-muted/40 px-2 py-1.5"
                      data-testid={`audit-dismissed-row-${f.id}`}
                    >
                      <span className="min-w-0 text-xs text-text-muted">{f.message}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="shrink-0 text-xs"
                        onClick={() => void handleUndoDismiss(f)}
                        data-testid={`audit-dismissed-undo-${f.id}`}
                      >
                        Undo
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
          <AuditDeepValidation
            site={site}
            siteReports={stageReports}
            findings={findings}
            result={result}
            reportsWithProviderRelevance={reportsWithProviderRelevance}
            stage={stage}
          />
        </>
      )}

      {result && findings.length === 0 && (
        <AuditDeepValidation
          site={site}
          siteReports={stageReports}
          findings={findings}
          result={result}
          reportsWithProviderRelevance={reportsWithProviderRelevance}
          stage={stage}
        />
      )}

      {/* Report quality — relocated from the reader's Insights tray
          (William 2026-07-28): sanity-finding review, fact-check, and
          generation quality are AUDIT concerns; the reader is the
          printable deliverable. Sits AFTER the site data-trust story —
          it audits the EDITORIAL layer (per-report findings, fact-check,
          cost), not site data. Deep-linked via ?tab=audit&report=<id>. */}
      <ReportQualityAuditSection
        siteReports={stageReports}
        siteId={site.id}
        siteDocuments={scopedSiteDocuments}
        onViewReport={onViewReport}
        focusReportId={qualityFocus?.reportId ?? null}
        focusNonce={qualityFocus?.nonce ?? 0}
        onRerunReport={(reportId) => {
          void regenerateReport(
            reportId,
            'Regeneration started — the audit re-runs when it completes.',
          );
        }}
      />

      {/* RP2 — dispute-tier-1 SED amendment. Routes through the ONE persist
          path (saveSEDAmendment); on success the audit recomputes so the
          R-78 finding clears (or re-points at the new authoritative value). */}
      <ResolveAmendmentDialog
        open={amendDraft != null}
        draft={amendDraft}
        onResolve={(amendment) => saveSEDAmendment(amendment)}
        onClose={() => setAmendDraft(null)}
        onResolved={() => {
          setAmendDraft(null);
          setReconcileNotice('SED value amended — recomputing the audit.');
          void runAuditForSite(site.id, { force: true });
        }}
      />

      {/* RECENT-ACTIVITY-RELOCATION — moved here from the Overview tab. */}
      <AuditRecentActivity
        site={site}
        siteReports={siteReports}
        siteDocuments={siteDocuments}
        onViewReport={onViewReport}
        onJumpToDocuments={onJumpToDocuments}
        onJumpToEvidence={onJumpToEvidence}
      />
    </div>
  );
}

/**
 * DEEP VALIDATION SURFACES — progressive disclosure (cockpit contract rule 4)
 * for the specialist audit panels (provenance, parcel variance/topology,
 * caveat honesty, claim/coherence/citation integrity, provider relevance).
 * Collapsed by default so the console + findings table stay the page's focus;
 * every panel is one click away, none are unmounted concerns while open.
 */
function DeepValidationSurfaces({ children, summary }: { children: import('react').ReactNode; summary?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <section
      className="rounded-2xl border border-border-subtle bg-background-elevated"
      data-testid="audit-deep-validation"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-2xl px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        data-testid="audit-deep-validation-toggle"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-text-muted" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-text-muted" aria-hidden />
        )}
        <span className="type-meta text-text-secondary">
          Deep validation surfaces
        </span>
        <span className="type-meta text-text-muted" data-testid="audit-deep-validation-summary">
          {summary ?? 'provenance · parcel · caveats · citations · coherence'}
        </span>
      </button>
      {open && (
        <div className="space-y-4 border-t border-border-subtle p-4">{children}</div>
      )}
    </section>
  );
}
